import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react'
import { Devices, Captures, DeviceEventType, TriageResult, CaptureSource, ManualCaptureOrigin, SessionIntent } from '../api'
import type { DeviceEventSummary, CaptureSummary, CaptureDetail, CaptureAnnotation, MediaSchema, RunSummary } from '../api'
import CaptureSidePanel from '../components/CaptureSidePanel'
import BurstView from '../components/BurstView'
import { parseTimelineHash } from '../lib/deviceTimelineUrl'

interface Props {
  hostname: string
  initialRunId?: string
  onNavigate: (target: string) => void
  onLogout: () => void
}

// ============================================================
// Event metadata
// ============================================================
type LaneKey = 'lifecycle' | 'captures' | 'ops' | 'taps' | 'connectivity'
const LANE_KEYS: LaneKey[] = ['lifecycle', 'captures', 'ops', 'taps', 'connectivity']

type EventMeta = {
  label: string
  lane: LaneKey
  glyph: GlyphName
  color: string
}

type GlyphName =
  | 'play' | 'square' | 'tare' | 'cap' | 'capdone' | 'cal'
  | 'sign' | 'warn' | 'up' | 'dot' | 'seq'
  | 'wifi_on' | 'wifi_off' | 'wifi_client' | 'clock' | 'flag'

// Frontend display labels are independent of wire field names. These match
// the prototype's vocabulary.
const TYPE_META: Partial<Record<DeviceEventType, EventMeta>> = {
  app_start:                 { label: 'app_start',                 glyph: 'play',    color: '#355E3B', lane: 'lifecycle' },
  app_stop:                  { label: 'app_stop',                  glyph: 'square',  color: '#9B3C3C', lane: 'lifecycle' },
  camera_error:              { label: 'camera_error',              glyph: 'warn',    color: '#9B3C3C', lane: 'lifecycle' },
  capture_triggered:         { label: 'capture_triggered',         glyph: 'cap',     color: '#D8613C', lane: 'captures'  },
  capture_complete:          { label: 'capture_complete',          glyph: 'capdone', color: '#355E3B', lane: 'captures'  },
  tare:                      { label: 'tare',                      glyph: 'tare',    color: '#5c3e35', lane: 'ops'       },
  clock_shift:               { label: 'clock_shift',               glyph: 'clock',   color: '#B07D3C', lane: 'ops'       },
  calibration_saved:         { label: 'calibration_saved',         glyph: 'cal',     color: '#B55050', lane: 'ops'       },
  signs_saved:               { label: 'signs_saved',               glyph: 'sign',    color: '#F0C070', lane: 'ops'       },
  config_changed:            { label: 'config_changed',            glyph: 'sign',    color: '#7A6FA0', lane: 'ops'       },
  data_push_complete:        { label: 'data_push_complete',        glyph: 'up',      color: '#a07a72', lane: 'ops'       },
  imu_tap:                   { label: 'imu_tap',                   glyph: 'dot',     color: '#5a8a60', lane: 'taps'      },
  imu_tap_sequence_complete: { label: 'imu_tap_sequence_complete', glyph: 'seq',     color: '#355E3B', lane: 'taps'      },
  // gps_fix has its own dedicated swim lane — `lane` here is for tooltip
  // metadata only; the regular EventLanes filters it out.
  gps_fix:                   { label: 'gps_fix',                   glyph: 'dot',     color: '#355E3B', lane: 'lifecycle' },
  // wifi_status_change / wifi_client_change carry connect/disconnect inside
  // the payload, so the glyph + color are *overridden per-event* via
  // metaForEvent() below. These entries are what the legend uses for the
  // wifi toggles.
  wifi_status_change:        { label: 'wifi_status_change',        glyph: 'wifi_on',     color: '#355E3B', lane: 'connectivity' },
  wifi_client_change:        { label: 'wifi_client_change',        glyph: 'wifi_client', color: '#5a8a60', lane: 'connectivity' },
  manual_capture_logged:     { label: 'manual_capture_logged',     glyph: 'capdone',     color: '#5a8a60', lane: 'captures'      },
  // session_intent's glyph/color depend on `payload.intent` — overridden per-
  // event in metaForEvent() below, same as the wifi types. This entry is
  // what the legend uses.
  session_intent:            { label: 'session_intent',            glyph: 'flag',        color: '#355E3B', lane: 'lifecycle'     },
  // Sticky capture-error state on the device (error LED held + app modal).
  error_state_entered:       { label: 'error_state_entered',       glyph: 'warn',        color: '#9B3C3C', lane: 'lifecycle'     },
  error_state_cleared:       { label: 'error_state_cleared',       glyph: 'capdone',     color: '#5a8a60', lane: 'lifecycle'     },
}

// Per-event override for cases where the glyph/color depends on payload
// fields (wifi_status_change / wifi_client_change branching on `connected`).
// All other event types fall back to TYPE_META.
function effectiveTsMs(c: CaptureSummary): number {
  if (c.source === CaptureSource.Device) return (c.captureStartTimestamp ?? 0) * 1000
  if (c.phoneLoggedAt) return c.phoneLoggedAt.getTime()
  return (c.captureStartTimestamp ?? 0) * 1000
}

function metaForEvent(ev: DeviceEventSummary): EventMeta | undefined {
  if (ev.eventType === 'wifi_status_change') {
    const connected = (ev.payload as { connected?: boolean })?.connected === true
    return connected
      ? { label: 'wifi_connected',    glyph: 'wifi_on',  color: '#355E3B', lane: 'connectivity' }
      : { label: 'wifi_disconnected', glyph: 'wifi_off', color: '#9B3C3C', lane: 'connectivity' }
  }
  if (ev.eventType === 'wifi_client_change') {
    const connected = (ev.payload as { connected?: boolean })?.connected === true
    return connected
      ? { label: 'wifi_client_connected',    glyph: 'wifi_client', color: '#5a8a60', lane: 'connectivity' }
      : { label: 'wifi_client_disconnected', glyph: 'wifi_client', color: '#7B2C2C', lane: 'connectivity' }
  }
  if (ev.eventType === 'session_intent') {
    const intent = (ev.payload as { intent?: string })?.intent
    return intent === 'fishing'
      ? { label: 'fishing', glyph: 'flag', color: '#355E3B', lane: 'lifecycle' }
      : { label: 'testing', glyph: 'flag', color: '#B07D3C', lane: 'lifecycle' }
  }
  return TYPE_META[ev.eventType as DeviceEventType]
}

const GLYPH_PATHS: Record<GlyphName, string> = {
  play:    '<path d="M -3 -3.5 L 3 0 L -3 3.5 Z" fill="currentColor"/>',
  square:  '<rect x="-3" y="-3" width="6" height="6" fill="currentColor"/>',
  tare:    '<text x="0" y="3.5" text-anchor="middle" font-family="JetBrains Mono" font-weight="600" font-size="9" fill="currentColor">T</text>',
  cap:     '<circle r="3" fill="none" stroke="currentColor" stroke-width="1.4"/><circle r="1.2" fill="currentColor"/>',
  capdone: '<circle r="3" fill="currentColor"/>',
  cal:     '<path d="M -3 3 L 0 -3 L 3 3 Z" fill="none" stroke="currentColor" stroke-width="1.4"/>',
  sign:    '<text x="0" y="3" text-anchor="middle" font-family="JetBrains Mono" font-weight="600" font-size="9" fill="currentColor">#</text>',
  warn:    '<path d="M 0 -3.5 L 3.5 3 L -3.5 3 Z" fill="currentColor"/><circle r="0.7" cy="0.8" fill="#fff"/>',
  up:      '<path d="M -2 1.5 L 0 -2 L 2 1.5 M 0 -2 V 2.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  dot:     '<circle r="2" fill="currentColor"/>',
  seq:     '<circle r="3" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M 0 -3 A 3 3 0 0 1 0 3" fill="currentColor"/>',
  wifi_on: '<g fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M -3.5 -1 A 4.5 4.5 0 0 1 3.5 -1"/><path d="M -2 1 A 2.5 2.5 0 0 1 2 1"/></g><circle r="0.9" cy="2.6" fill="currentColor"/>',
  wifi_off:'<g fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"><path d="M -3.5 -1 A 4.5 4.5 0 0 1 3.5 -1"/><path d="M -2 1 A 2.5 2.5 0 0 1 2 1"/></g><circle r="0.9" cy="2.6" fill="currentColor"/><line x1="-3.6" y1="3.6" x2="3.6" y2="-3.6" stroke="currentColor" stroke-width="1.2"/>',
  wifi_client: '<circle cx="-1.4" cy="0" r="2.2" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="1.4" cy="0" r="2.2" fill="none" stroke="currentColor" stroke-width="1.2"/>',
  clock:   '<circle r="3.2" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M 0 -1.8 V 0 L 1.6 1" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>',
  flag:    '<path d="M -2.5 -3.5 V 3.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M -2.5 -3.5 L 3 -2 L -2.5 -0.5 Z" fill="currentColor"/>',
}

// Trace palette — drawn from the project's bear/coral/gold/pine + ink earth tones.
const TRACE_COLORS = {
  weight:    '#9B3C3C', weightSmoothed: '#C86060',
  accX:      '#D8613C', accY: '#F0C070', accZ: '#355E3B',
  gyrX:      '#7B2C2C', gyrY: '#5a8a60', gyrZ: '#5c3e35',
  cpu:       '#B55050', mem:  '#7a5a52', swap: '#a07a72',
} as const

// Per-channel weight/raw colors — generated procedurally for N channels.
function channelColor(idx: number, kind: 'weight' | 'raw'): string {
  const wPalette = ['#9B3C3C', '#D8613C', '#F0C070', '#355E3B', '#5a8a60', '#7B2C2C']
  const rPalette = ['#a07a72', '#7a5a52', '#5c3e35', '#3d2a22', '#B55050', '#C86060']
  return (kind === 'weight' ? wPalette : rPalette)[idx % wPalette.length]
}

// Weight formatting honors the user's session g/lb preference. Shared helper
// across the panel y-axis ticks, cursor readout, and hover tooltip rows.
const G_PER_LB = 453.592
function fmtWeight(grams: number, units: 'g' | 'lb', digits = 1): string {
  if (units === 'lb') return `${(grams / G_PER_LB).toFixed(2)} lb`
  return `${grams.toFixed(digits)} g`
}
function convertGramsForAxis(grams: number, units: 'g' | 'lb'): number {
  return units === 'lb' ? grams / G_PER_LB : grams
}

// ============================================================
// Time helpers (UTC / local / custom-offset)
// ============================================================
// `Tz` is either one of two presets or a custom offset in minutes from UTC.
// Custom is for "I'm in NY but the device is in BC" — pick a fixed offset
// that approximates the device's wall-clock at the moment of the events.
// Doesn't capture DST transitions; for log debugging that's fine, and IANA
// zone-name parsing isn't worth the dependency.
type Tz = 'utc' | 'local' | { offsetMinutes: number }

function tzParts(d: Date, tz: Tz): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  if (tz === 'utc') {
    return { y: d.getUTCFullYear(), mo: d.getUTCMonth(), d: d.getUTCDate(),
             h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds() }
  }
  if (tz === 'local') {
    return { y: d.getFullYear(), mo: d.getMonth(), d: d.getDate(),
             h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds() }
  }
  // Custom offset: shift the timestamp by offsetMinutes, then read UTC parts.
  const shifted = new Date(d.getTime() + tz.offsetMinutes * 60_000)
  return { y: shifted.getUTCFullYear(), mo: shifted.getUTCMonth(), d: shifted.getUTCDate(),
           h: shifted.getUTCHours(), mi: shifted.getUTCMinutes(), s: shifted.getUTCSeconds() }
}

function tzLabel(tz: Tz): string {
  if (tz === 'utc') return 'UTC'
  if (tz === 'local') return 'local'
  return formatOffset(tz.offsetMinutes)
}

function fmtTime(ts: number, tz: Tz): string {
  const p = tzParts(new Date(ts), tz)
  return [p.h, p.mi, p.s].map(n => String(n).padStart(2, '0')).join(':')
}
function fmtTimeShort(ts: number, tz: Tz): string {
  const p = tzParts(new Date(ts), tz)
  return String(p.h).padStart(2, '0') + ':' + String(p.mi).padStart(2, '0')
}
function fmtDate(ts: number, tz: Tz): string {
  const p = tzParts(new Date(ts), tz)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${months[p.mo]} ${p.d}`
}
function fmtFull(ts: number, tz: Tz): string {
  return `${fmtDate(ts, tz)} ${fmtTime(ts, tz)} ${tzLabel(tz)}`
}

// "yyyy-MM-ddTHH:mm" string for a <input type="datetime-local"> field, in the
// chosen tz. parseDtLocal does the inverse.
function dtLocalString(ts: number, tz: Tz): string {
  const p = tzParts(new Date(ts), tz)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${p.y}-${pad(p.mo + 1)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`
}
function parseDtLocal(s: string, tz: Tz): number {
  if (!s) return NaN
  if (tz === 'utc') return Date.parse(s + ':00Z')
  if (tz === 'local') return Date.parse(s + ':00')
  // Custom offset: append the offset string explicitly so JS parses it as UTC-shifted.
  const m = tz.offsetMinutes
  const sign = m >= 0 ? '+' : '-'
  const abs = Math.abs(m)
  const offsetStr = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`
  return Date.parse(s + ':00' + offsetStr)
}
// Compact age string: "12s ago", "3m ago", "2h ago", "5d ago", "in 3s" etc.
function fmtAge(secs: number): string {
  const sign = secs >= 0 ? '' : '−'
  const a = Math.abs(secs)
  let out: string
  if (a < 1) out = `${a.toFixed(1)}s`
  else if (a < 60) out = `${a.toFixed(0)}s`
  else if (a < 3600) out = `${(a / 60).toFixed(0)}m`
  else if (a < 86400) out = `${(a / 3600).toFixed(1)}h`
  else out = `${(a / 86400).toFixed(1)}d`
  return secs >= 0 ? `${out} ago` : `in ${sign}${out}`
}

function fmtDuration(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`
  const m = Math.floor(secs / 60), s = Math.round(secs % 60)
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60), mm = m % 60
  return mm > 0 ? `${h}h ${mm}m` : `${h}h`
}

// ============================================================
// Range shorthand
// ============================================================
// Default fallback window when there's no run data and no URL range — 24h ending now.
function defaultRange(now = Date.now()): { start: number; end: number } {
  return { start: now - 24 * 3600_000, end: now }
}

// ============================================================
// URL state — ?from=ISO&to=ISO query in the hash
// ============================================================
function parseRangeFromHash(): { start: number; end: number } | null {
  const hash = window.location.hash
  const i = hash.indexOf('?')
  if (i < 0) return null
  const params = new URLSearchParams(hash.slice(i + 1))
  const fromStr = params.get('from')
  const toStr = params.get('to')
  if (!fromStr || !toStr) return null
  const start = Date.parse(fromStr)
  const end = Date.parse(toStr)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  return { start, end }
}
function updateHashRange(start: number, end: number) {
  const hash = window.location.hash
  const i = hash.indexOf('?')
  const path = i < 0 ? hash : hash.slice(0, i)
  const params = new URLSearchParams(i < 0 ? '' : hash.slice(i + 1))
  params.set('from', new Date(start).toISOString())
  params.set('to',   new Date(end).toISOString())
  // Preserve capture param if present (focused capture state)
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}${path}?${params.toString()}`)
}

// Sync the focused capture to the URL without disturbing from/to/run_id.
function updateHashCapture(captureId: string | null) {
  const hash = window.location.hash
  const i = hash.indexOf('?')
  const path = i < 0 ? hash : hash.slice(0, i)
  const params = new URLSearchParams(i < 0 ? '' : hash.slice(i + 1))
  if (captureId) params.set('capture', captureId)
  else params.delete('capture')
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}${path}?${params.toString()}`)
}

// ============================================================
// Burst view: ?burst=<capture id> swaps the lanes for that capture's burst.
function parseBurstFromHash(): string | null {
  const hash = window.location.hash
  const i = hash.indexOf('?')
  return i < 0 ? null : new URLSearchParams(hash.slice(i + 1)).get('burst')
}
function updateHashBurst(captureId: string | null) {
  const hash = window.location.hash
  const i = hash.indexOf('?')
  const path = i < 0 ? hash : hash.slice(0, i)
  const params = new URLSearchParams(i < 0 ? '' : hash.slice(i + 1))
  if (captureId) params.set('burst', captureId)
  else params.delete('burst')
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}${path}?${params.toString()}`)
}

// ============================================================
// SVG path computation — lifted from prototype, decimates and lifts on null gaps.
// ============================================================
type Pt = { t: number; v: number | null }

type Series = {
  weight: Pt[]; weightS: Pt[]
  accX: Pt[]; accY: Pt[]; accZ: Pt[]
  gyrX: Pt[]; gyrY: Pt[]; gyrZ: Pt[]
  cpu: Pt[]; mem: Pt[]; swap: Pt[]
  cellWeight: Pt[][]; cellRaw: Pt[][]
}

// Binary-search the nearest sample by time. Returns null when arr is empty.
function nearestVal(arr: Pt[], t: number): number | null {
  if (arr.length === 0) return null
  let lo = 0, hi = arr.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (arr[mid].t < t) lo = mid + 1
    else hi = mid
  }
  const a = arr[lo], b = arr[Math.max(0, lo - 1)]
  const pick = Math.abs(a.t - t) < Math.abs(b.t - t) ? a : b
  return pick.v
}

function pathFor(arr: Pt[], viewStart: number, viewEnd: number, w: number, h: number, vMin: number, vMax: number, yPad = 4): string {
  const range = vMax - vMin || 1
  const innerH = h - yPad * 2
  const span = viewEnd - viewStart
  if (span <= 0 || w <= 0) return ''
  let d = ''
  let lifted = true
  for (let i = 0; i < arr.length; i++) {
    const p = arr[i]
    if (p.v === null || p.v === undefined) { lifted = true; continue }
    if (p.t < viewStart || p.t > viewEnd) { lifted = true; continue }
    const x = ((p.t - viewStart) / span) * w
    // Clamp to the inner band so an out-of-range point (e.g. a spike when the
    // axis is scaled to the data's bulk) pins to the edge instead of drawing
    // outside the panel. No-op for panels whose axis already fits min/max.
    const y = Math.min(h - yPad, Math.max(yPad, yPad + (1 - (p.v - vMin) / range) * innerH))
    d += (lifted ? ' M ' : ' L ') + x.toFixed(1) + ' ' + y.toFixed(1)
    lifted = false
  }
  return d
}

// Pick 3-4 round-number tick values inside [min, max].
function niceTicks(min: number, max: number, target = 3): number[] {
  const span = max - min
  if (span <= 0) return [min]
  const rough = span / target
  const exp = Math.floor(Math.log10(rough))
  const base = Math.pow(10, exp)
  const candidates = [1, 2, 5, 10].map(m => m * base)
  const step = candidates.find(c => span / c <= target + 1) ?? candidates[candidates.length - 1]
  const first = Math.ceil(min / step) * step
  const out: number[] = []
  for (let v = first; v <= max + 1e-9; v += step) {
    // Clean up tiny float drift
    out.push(Math.round(v / step) * step)
  }
  return out
}

function decimate<T>(arr: T[], target: number): T[] {
  if (arr.length <= target) return arr
  const step = arr.length / target
  const out: T[] = []
  for (let i = 0; i < target; i++) out.push(arr[Math.floor(i * step)])
  out.push(arr[arr.length - 1])
  return out
}

function tToX(t: number, viewStart: number, viewEnd: number, w: number): number {
  return ((t - viewStart) / (viewEnd - viewStart)) * w
}

// ============================================================
// Glyph helpers (matches prototype, used for both lane glyphs and legend chips)
// ============================================================
function GlyphSvg({ name, color, size = 16 }: { name: GlyphName; color: string; size?: number }) {
  const half = size / 2
  return (
    <svg viewBox={`${-half} ${-half} ${size} ${size}`} width={size} height={size} style={{ color }}
         dangerouslySetInnerHTML={{ __html: GLYPH_PATHS[name] }} />
  )
}

// ============================================================
// Resize observer hook — measure a ref'd element
// ============================================================
function useElementSize<T extends HTMLElement>(ref: React.RefObject<T | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight })
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return size
}

// ============================================================
// MAIN PAGE
// ============================================================
const LABEL_W = 140
const RAIL_W  = 296
const HEIGHTS = { weight: 150, acc: 105, gyr: 105, sysinfo: 70, cells: 150 } as const

// Auto-refresh poll cadence, and the window LIVE mode follows (last hour — a
// zoom where following reads cleanly without re-sample jitter).
const POLL_INTERVAL_MS = 10_000
const LIVE_SPAN_MS = 60 * 60 * 1000

// Outlier exclusion (weight/load-cell panels): scale the y-axis to the
// [pct, 1-pct] percentile range (symmetric, so both low and high spikes are
// excluded) so a spike doesn't flatten the real signal. Per-panel radio picks
// the level; pct=0 means "none" (raw min/max). Default p99.
const OUTLIER_LEVELS: { label: string; pct: number }[] = [
  { label: 'p99', pct: 0.01 },
  { label: 'p95', pct: 0.05 },
  { label: 'none', pct: 0 },
]
const DEFAULT_OUTLIER_PCT = 0.01

// Compute the actual time window for a run from its event extent. Falls
// back to startedAt only if the event extent isn't populated. Returns null
// when there's nothing to render.
function runWindow(run: RunSummary): { start: number; end: number } | null {
  const startMs =
    run.earliestEventTimestamp ? run.earliestEventTimestamp.getTime() :
    run.startedAt != null ? run.startedAt * 1000 : null
  const endMs =
    run.latestEventTimestamp ? run.latestEventTimestamp.getTime() : null
  if (startMs == null || endMs == null) return null
  // Pad ~10% of the span on each end (mirrors the rail's "Runs in window"
  // behavior). Floor at 5 seconds so very short runs still get visible
  // breathing room.
  const span = Math.max(endMs - startMs, 1000)
  const pad = Math.max(5_000, span * 0.1)
  return { start: startMs - pad, end: endMs + pad }
}

export default function DeviceTimelinePage({ hostname, initialRunId, onNavigate, onLogout }: Props) {
  const [tz, setTz] = useState<Tz>('utc')
  const [{ start: viewStart, end: viewEnd }, _setView] = useState(() => parseRangeFromHash() ?? defaultRange())

  // Live-tail plumbing: a window mirror so the refetchers stay stable, an
  // overlap guard, per-fetcher request-id guards (ignore stale responses), and
  // a first-load flag (only the initial events fetch shows the loading bar;
  // pans and background polls never flash it).
  const viewRef = useRef({ start: viewStart, end: viewEnd })
  viewRef.current = { start: viewStart, end: viewEnd }
  const isPollingRef = useRef(false)
  const hasLoadedRef = useRef(false)
  const eventsReqRef = useRef(0)
  const capturesReqRef = useRef(0)

  // Live mode: when on, the poll advances the window to [now-1h, now] each
  // tick (a zoom where following reads cleanly). Any manual view change turns
  // it off so it never fights the user.
  const [live, setLive] = useState(false)
  const liveRef = useRef(live)
  liveRef.current = live

  // Any user-driven view change exits live mode so following never fights the
  // user. Only the live poll advances the window via the raw _setView.
  const setView = useCallback((v: { start: number; end: number }) => {
    setLive(false)
    _setView(v)
  }, [])

  // Sync view → URL on every change (replaceState, no history pollution).
  useEffect(() => { updateHashRange(viewStart, viewEnd) }, [viewStart, viewEnd])

  // URL → view: react to external hash changes (back/forward, manual URL edits,
  // navigations from other pages). replaceState doesn't fire hashchange so our
  // own URL writes don't loop here.
  useEffect(() => {
    const onHashChange = () => {
      const r = parseRangeFromHash()
      if (r && (r.start !== viewStart || r.end !== viewEnd)) {
        setView(r)
      }
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [viewStart, viewEnd, setView])
  // Per-channel weight series visible by default; channel-raw still hidden
  // (debug-only). Other discrete event types un-hidden by default too.
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  // Per-panel outlier-exclusion percentile (0 = none). Defaults to p99 on the
  // panels that offer it (weight / cells-weight / cells-raw).
  const [outlierPct, setOutlierPct] = useState<Record<string, number>>({
    weight: DEFAULT_OUTLIER_PCT,
    'cells-weight': DEFAULT_OUTLIER_PCT,
    'cells-raw': DEFAULT_OUTLIER_PCT,
  })
  const [events, setEvents] = useState<DeviceEventSummary[]>([])
  const [totalsPerType, setTotalsPerType] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hoverX, setHoverX] = useState<number | null>(null)
  const [hoverEvent, setHoverEvent] = useState<{ ev: DeviceEventSummary; x: number; y: number } | null>(null)
  const [pinnedEvent, setPinnedEvent] = useState<{ ev: DeviceEventSummary; x: number; y: number } | null>(null)
  const visibleEvent = pinnedEvent ?? hoverEvent
  const [allHostnames, setAllHostnames] = useState<string[]>([])

  // Runs for this device — drives the run picker. Backend returns DESC.
  const [runs, setRuns] = useState<RunSummary[]>([])
  const [currentRunId, setCurrentRunId] = useState<string | null>(initialRunId ?? null)

  // g/lb units, persisted across sessions.
  const [units, setUnits] = useState<'g' | 'lb'>(() => {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem('kodiak-weight-units') : null
    return v === 'lb' ? 'lb' : 'g'
  })
  useEffect(() => { window.localStorage.setItem('kodiak-weight-units', units) }, [units])

  // Catch modal: pre-fetch catches for the device, look up by capture_id on
  // demand, and render CaptureSidePanel as a modal overlay.
  const [captures, setCaptures] = useState<CaptureSummary[]>([])
  const [captureDetails, setCaptureDetails] = useState<Record<string, CaptureDetail>>({})
  const [openCaptureId, setOpenCaptureId] = useState<string | null>(null)
  // focusedCaptureId: the capture highlighted/outlined in the timeline (from URL or nav).
  const [focusedCaptureId, setFocusedCaptureId] = useState<string | null>(null)
  // Burst view: the capture whose burst replaces the lanes, or null for the timeline.
  const [burstCaptureId, setBurstCaptureId] = useState<string | null>(() => parseBurstFromHash())
  useEffect(() => { updateHashBurst(burstCaptureId) }, [burstCaptureId])
  useEffect(() => {
    const onHashChange = () => setBurstCaptureId(parseBurstFromHash())
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  // Shared refetchers for the initial load and the live-tail poll. Events read
  // the current window from viewRef so the callback stays stable per host.
  // `background` fetches skip the loading bar and stay silent on error.
  const refetchEvents = useCallback((opts?: { background?: boolean }) => {
    const background = opts?.background ?? false
    const { start, end } = viewRef.current
    const reqId = ++eventsReqRef.current
    if (!background) { setLoading(true); setError(null) }
    return Devices.listDeviceEvents({
      listDeviceEventsRequest: {
        hostname,
        fromTimestamp: new Date(start),
        toTimestamp: new Date(end),
        maxPerType: 500,
      },
    })
      .then(resp => {
        if (reqId !== eventsReqRef.current) return
        setEvents(resp.events)
        setTotalsPerType(resp.totalsPerType)
        if (!background) setLoading(false)
      })
      .catch((err: Error) => {
        if (reqId !== eventsReqRef.current) return
        if (!background) { setError(err.message); setLoading(false) }
      })
  }, [hostname])

  // Captures: fetch ALL of the device's captures (not windowed) so prev/next
  // nav spans everything. includeThumbnails:false skips the per-row presigned
  // URL — the timeline never renders list thumbnails — so the full fetch is
  // cheap. Empty includeTriageResults surfaces discarded captures too. Merged
  // by id so deep-link-resolved captures survive a refetch.
  const refetchCaptures = useCallback(() => {
    const reqId = ++capturesReqRef.current
    return Captures.listCaptures({
      listCapturesRequest: {
        includeDeviceIds: [hostname],
        includeAllDevices: false,
        includeTriageResults: [],
        includeThumbnails: false,
      },
    })
      .then(r => {
        if (reqId !== capturesReqRef.current) return
        setCaptures(prev => {
          const byId = new Map(prev.map(c => [c.captureId, c]))
          for (const c of r.captures) byId.set(c.captureId, c)
          return Array.from(byId.values())
        })
      })
      .catch(() => {})
  }, [hostname])

  const refetchRuns = useCallback(() => {
    return Devices.getDevices({ getDevicesRequest: { hostnames: [hostname] } })
      .then(resp => setRuns((resp.devices[0]?.runs ?? []).filter(r => (r.eventCount ?? 0) > 0)))
      .catch(() => {})
  }, [hostname])

  // Events refetch when the window (or device) changes — debounced so dragging
  // the time axis fires one request when it settles, not one per frame. The
  // first load shows the loading bar; every later fetch is background.
  useEffect(() => {
    const id = setTimeout(() => {
      refetchEvents({ background: hasLoadedRef.current })
      hasLoadedRef.current = true
    }, 250)
    return () => clearTimeout(id)
  }, [hostname, viewStart, viewEnd, refetchEvents])

  // Captures are window-independent — fetch once per device (live-tail also
  // refreshes them on its poll).
  useEffect(() => {
    refetchCaptures()
  }, [hostname, refetchCaptures])

  const captureById = useMemo(() => {
    const m: Record<string, CaptureSummary> = {}
    for (const c of captures) m[c.captureId] = c
    return m
  }, [captures])

  const openedCapture = openCaptureId ? captureById[openCaptureId] ?? null : null
  const openedDetail = openedCapture ? captureDetails[openedCapture.captureId] ?? null : null

  // Lazy-load detail when a catch is opened.
  useEffect(() => {
    if (!openedCapture) return
    if (captureDetails[openedCapture.captureId]) return
    const id = openedCapture.captureId
    Captures.getCaptures({ getCapturesRequest: { captureIds: [id] } })
      .then(r => { if (r.captures[0]) setCaptureDetails(prev => ({ ...prev, [id]: r.captures[0] })) })
      .catch(() => {})
  }, [openedCapture, captureDetails])

  // Sorted captures array for prev/next navigation. Effective timestamp:
  //   DEVICE → captureStartTimestamp (epoch seconds); MANUAL → phoneLoggedAt (Date).
  const sortedCaptures = useMemo<CaptureSummary[]>(() => {
    return [...captures].sort((a, b) => effectiveTsMs(a) - effectiveTsMs(b))
  }, [captures])

  // Resolve a capture from the URL's `capture` param on mount and hash changes.
  // Sets openCaptureId and recenters the timeline on the resolved target.
  function recenterOnCapture(c: CaptureSummary) {
    const ts = effectiveTsMs(c)
    if (!ts) return
    const halfSpan = 10 * 60_000 // ±10 min window
    setView({ start: ts - halfSpan, end: ts + halfSpan })
  }

  const captureByIdRef = useRef(captureById)
  captureByIdRef.current = captureById

  useEffect(() => {
    const { captureId: urlCaptureId } = parseTimelineHash()
    if (!urlCaptureId) return
    let cancelled = false

    async function resolve() {
      const resp = await Captures.getCaptures({ getCapturesRequest: { captureIds: [urlCaptureId!] } })
      if (cancelled) return
      const cap = resp.captures[0]
      if (!cap) return
      setCaptureDetails(prev => ({ ...prev, [cap.captureId]: cap }))

      let targetId = cap.captureId

      if (cap.source === CaptureSource.Manual && cap.linkedCaptureId) {
        // Linked manual → focus the linked device capture
        const linkedResp = await Captures.getCaptures({ getCapturesRequest: { captureIds: [cap.linkedCaptureId] } })
        if (cancelled) return
        const linked = linkedResp.captures[0]
        if (linked) {
          setCaptureDetails(prev => ({ ...prev, [linked.captureId]: linked }))
          targetId = linked.captureId
          // Add linked capture to in-memory list if not already there
          setCaptures(prev => prev.some(x => x.captureId === linked.captureId) ? prev : [...prev, linked])
        }
      } else if (cap.source === CaptureSource.Manual && cap.manualOrigin !== ManualCaptureOrigin.Pi) {
        // Cloud-only manual with no linked capture — no timeline anchor, skip
        return
      }

      // Add the capture to in-memory list if not already present
      setCaptures(prev => prev.some(x => x.captureId === cap.captureId) ? prev : [...prev, cap])

      setFocusedCaptureId(targetId)
      setOpenCaptureId(targetId)
      const target = captureByIdRef.current[targetId] ?? cap
      recenterOnCapture(target)
    }

    resolve().catch(() => {})
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostname])

  // Sync openCaptureId → URL capture param so deep links stay current.
  useEffect(() => {
    updateHashCapture(openCaptureId)
    if (openCaptureId) setFocusedCaptureId(openCaptureId)
  }, [openCaptureId])

  const handleOpenCatch = useCallback((captureId: string) => setOpenCaptureId(captureId), [])
  const handleOpenBurst = useCallback((captureId: string) => {
    setPinnedEvent(null)
    setOpenCaptureId(null)
    setBurstCaptureId(captureId)
  }, [])

  // Modal edit handlers — same pattern as CapturesPage but scoped to the
  // small slice of state we keep here.
  async function handleTriageChange(captureId: string, result: TriageResult) {
    setCaptures(cats => cats.map(x => x.captureId === captureId ? { ...x, triageResult: result } : x))
    try {
      await Captures.annotateCaptures({
        annotateCapturesRequest: { annotations: [{ captureId, triageResult: result }] },
      })
    } catch {/* keep optimistic state */}
  }
  async function handleAnnotationSave(captureId: string, annotation: CaptureAnnotation) {
    setCaptures(cats => cats.map(x => x.captureId === captureId ? { ...x, annotation } : x))
    try {
      await Captures.annotateCaptures({
        annotateCapturesRequest: { annotations: [{ captureId, annotation }] },
      })
    } catch {/* keep optimistic state */}
  }
  function handleMediaUploaded(captureId: string, media: MediaSchema) {
    setCaptureDetails(prev => {
      const d = prev[captureId]
      if (!d) return prev
      return { ...prev, [captureId]: { ...d, media: [...d.media, media] } }
    })
  }
  function handleLinked(_manualCatchId: string, _deviceCatchId: string) {
    // Linking from the timeline is rare; close the modal so the user can
    // re-click and see the merged result.
    setOpenCaptureId(null)
  }

  // Prev/next through sortedCaptures.
  const openedIdx = openCaptureId ? sortedCaptures.findIndex(c => c.captureId === openCaptureId) : -1
  const prevDisabled = openedIdx <= 0
  const nextDisabled = openedIdx < 0 || openedIdx >= sortedCaptures.length - 1

  function handlePrev() {
    if (prevDisabled) return
    const c = sortedCaptures[openedIdx - 1]
    setOpenCaptureId(c.captureId)
    recenterOnCapture(c)
  }
  function handleNext() {
    if (nextDisabled) return
    const c = sortedCaptures[openedIdx + 1]
    setOpenCaptureId(c.captureId)
    recenterOnCapture(c)
  }
  const [customOpen, setCustomOpen] = useState(false)
  const customAnchorRef = useRef<HTMLButtonElement | null>(null)
  const [railOpen, setRailOpen] = useState(true)

  // Click-outside-tooltip-and-not-on-glyph → unpin.
  useEffect(() => {
    if (!pinnedEvent) return
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('[data-event-tooltip]')) return
      if (t.closest('[data-event-glyph]')) return
      setPinnedEvent(null)
    }
    const id = setTimeout(() => document.addEventListener('mousedown', onDocDown), 0)
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDocDown) }
  }, [pinnedEvent])

  // Load device list once for the dropdown.
  useEffect(() => {
    let cancelled = false
    Devices.listDevices()
      .then(resp => { if (!cancelled) setAllHostnames(resp.devices.map(d => d.hostname).sort()) })
      .catch(() => { /* silent — dropdown just stays at current host */ })
    return () => { cancelled = true }
  }, [])

  const panelsRef = useRef<HTMLDivElement | null>(null)
  const panelsCanvasRef = useRef<HTMLDivElement | null>(null)
  const { w: canvasW } = useElementSize(panelsCanvasRef)

  // Shift the view by a fraction of its current span (negative = back, positive = forward).
  function panBy(fraction: number) {
    const span = viewEnd - viewStart
    const delta = fraction * span
    setView({ start: viewStart + delta, end: viewEnd + delta })
  }
  // Scale the view's span (factor>1 = zoom out, factor<1 = zoom in), keeping center fixed.
  function zoomBy(factor: number) {
    const span = viewEnd - viewStart
    const center = (viewStart + viewEnd) / 2
    const newSpan = Math.max(60_000, span * factor)   // floor: 1 minute
    setView({ start: center - newSpan / 2, end: center + newSpan / 2 })
  }

  // (Events/captures fetching moved to the shared refetchers above, driven by
  // the window-change effects and the live-tail poll.)

  // Load runs for this device once for the run picker. Backend returns
  // DESC (most recent first).
  useEffect(() => {
    let cancelled = false
    Devices.getDevices({ getDevicesRequest: { hostnames: [hostname] } })
      .then((resp) => {
        if (cancelled) return
        // Filter out legacy runs with no events — they're orphans from older
        // ingestion paths and have nothing to render.
        const list: RunSummary[] = (resp.devices[0]?.runs ?? []).filter(
          r => (r.eventCount ?? 0) > 0
        )
        setRuns(list)
        // Default-select the most recent run on first load if no run was
        // already specified via the URL — but skip if the URL is focusing a
        // specific capture, since the capture resolver will recenter the view
        // and we'd otherwise race and clobber it.
        const mostRecent = list[0]
        const focusedCapture = parseTimelineHash().captureId
        if (!currentRunId && !focusedCapture && mostRecent) {
          const span = runWindow(mostRecent)
          if (span) {
            setCurrentRunId(mostRecent.runId)
            setView(span)
          }
        }
      })
      .catch(() => { /* silent — picker just won't have entries */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostname])

  // Auto-refresh poll. Paused while the tab is hidden (one catch-up refetch on
  // refocus). In LIVE mode it advances the window to [now-1h, now] (the
  // window-change effect then refetches events); otherwise it refreshes the
  // current window's data in place — no window movement, so no re-sample
  // jitter. Captures + runs refresh every tick regardless. Overlap-guarded.
  useEffect(() => {
    const tick = async () => {
      if (document.hidden || isPollingRef.current) return
      isPollingRef.current = true
      try {
        if (liveRef.current) {
          const now = Date.now()
          _setView({ start: now - LIVE_SPAN_MS, end: now })
          await Promise.all([refetchCaptures(), refetchRuns()])
        } else {
          await Promise.all([
            refetchEvents({ background: true }),
            refetchCaptures(),
            refetchRuns(),
          ])
        }
      } finally {
        isPollingRef.current = false
      }
    }
    const iv = setInterval(tick, POLL_INTERVAL_MS)
    const onVis = () => { if (!document.hidden) tick() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis) }
  }, [refetchEvents, refetchCaptures, refetchRuns])

  // Enter live mode: jump to the last hour and start following now.
  const enterLive = useCallback(() => {
    const now = Date.now()
    setLive(true)
    _setView({ start: now - LIVE_SPAN_MS, end: now })
  }, [])

  function selectRun(runId: string) {
    const run = runs.find(r => r.runId === runId)
    if (!run) return
    const span = runWindow(run)
    if (!span) return
    setCurrentRunId(runId)
    setView(span)
    // Stamp run_id into URL so deep-links round-trip.
    onNavigate(`#device/${encodeURIComponent(hostname)}/timeline?run_id=${encodeURIComponent(runId)}`)
  }
  function stepRun(direction: -1 | 1) {
    if (!currentRunId || runs.length === 0) return
    const i = runs.findIndex(r => r.runId === currentRunId)
    if (i < 0) return
    // runs are DESC: index 0 = newest, index N-1 = oldest.
    // direction=+1 → newer (lower index); direction=-1 → older (higher index).
    const next = runs[i - direction]
    if (next) selectRun(next.runId)
  }

  // ---------- derived: continuous series, sorted by time ----------
  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.eventTimestamp.getTime() - b.eventTimestamp.getTime()),
    [events]
  )

  const series: Series = useMemo(() => {
    const weight: Pt[]     = []
    const weightS: Pt[]    = []
    const accX: Pt[] = [], accY: Pt[] = [], accZ: Pt[] = []
    const gyrX: Pt[] = [], gyrY: Pt[] = [], gyrZ: Pt[] = []
    const cpu: Pt[] = [], mem: Pt[] = [], swap: Pt[] = []
    // Per-channel: discovered lazily.
    const cellWeight: Pt[][] = []
    const cellRaw: Pt[][]    = []

    // Break every continuous series at run boundaries so pathFor lifts the
    // pen (it lifts on null) — a line never interpolates across two runs.
    let prevRunId: string | null | undefined = undefined
    const breakAll = (t: number) => {
      const scalars = [weight, weightS, accX, accY, accZ, gyrX, gyrY, gyrZ, cpu, mem, swap]
      for (const arr of [...scalars, ...cellWeight, ...cellRaw]) {
        if (arr.length && arr[arr.length - 1].v !== null) arr.push({ t, v: null })
      }
    }

    for (const ev of sortedEvents) {
      const t = ev.eventTimestamp.getTime()
      if (prevRunId !== undefined && ev.runId !== prevRunId) breakAll(t)
      prevRunId = ev.runId
      const p = ev.payload
      switch (p.type) {
        case 'weight_read': {
          weight.push({ t, v: p.weightGrams })
          weightS.push({ t, v: p.smoothedWeightGrams })
          const channels = p.channelReadings
          if (channels) {
            for (let i = 0; i < channels.length; i++) {
              while (cellWeight.length <= i) { cellWeight.push([]); cellRaw.push([]) }
              cellWeight[i].push({ t, v: channels[i].weightGrams })
              cellRaw[i].push({ t, v: channels[i].rawValue })
            }
          }
          break
        }
        case 'imu_read':
          accX.push({ t, v: p.acc?.[0] ?? null })
          accY.push({ t, v: p.acc?.[1] ?? null })
          accZ.push({ t, v: p.acc?.[2] ?? null })
          gyrX.push({ t, v: p.gyr?.[0] ?? null })
          gyrY.push({ t, v: p.gyr?.[1] ?? null })
          gyrZ.push({ t, v: p.gyr?.[2] ?? null })
          break
        case 'sysinfo':
          cpu.push({ t, v: p.cpuPct })
          mem.push({ t, v: p.memPct })
          swap.push({ t, v: p.swapPct })
          break
      }
    }
    return { weight, weightS, accX, accY, accZ, gyrX, gyrY, gyrZ, cpu, mem, swap, cellWeight, cellRaw }
  }, [sortedEvents])

  // ---------- derived: discrete event grouping ----------
  const discreteEvents = useMemo(() => {
    return sortedEvents.filter(ev => {
      if (ev.eventType === 'gps_fix') return false   // dedicated GPS swim lane
      const meta = metaForEvent(ev)
      return meta && !hidden.has(ev.eventType)
    })
  }, [sortedEvents, hidden])

  const discreteByLane = useMemo(() => {
    const byLane: Record<LaneKey, DeviceEventSummary[]> = {
      lifecycle: [], captures: [], ops: [], taps: [], connectivity: [],
    }
    for (const ev of discreteEvents) {
      const meta = metaForEvent(ev)
      if (meta) byLane[meta.lane].push(ev)
    }
    return byLane
  }, [discreteEvents])

  const captureSpans = useMemo(() => {
    const byCap: Record<string, { trigger?: DeviceEventSummary; complete?: DeviceEventSummary }> = {}
    for (const ev of sortedEvents) {
      const p = ev.payload as { type: string; captureId?: string; ok?: boolean }
      if (p.type === 'capture_triggered' && p.captureId) {
        if (!byCap[p.captureId]) byCap[p.captureId] = {}
        byCap[p.captureId].trigger = ev
      } else if (p.type === 'capture_complete' && p.captureId) {
        if (!byCap[p.captureId]) byCap[p.captureId] = {}
        byCap[p.captureId].complete = ev
      } else if (p.type === 'manual_capture_logged' && p.captureId) {
        // Manual captures only have a single "complete" event (no trigger).
        if (!byCap[p.captureId]) byCap[p.captureId] = {}
        byCap[p.captureId].complete = ev
      }
    }
    return byCap
  }, [sortedEvents])

  const gpsFixes = useMemo(
    () => sortedEvents.filter(ev => ev.eventType === 'gps_fix'),
    [sortedEvents]
  )

  // Runs derived from the events currently in view (used by the rail's
  // RunsList — distinct from the master `runs` list driving the picker).
  const runsInView = useMemo(() => {
    const seen = new Set<string>()
    const list: { runId: string; firstSeen: number; lastSeen: number }[] = []
    for (const ev of sortedEvents) {
      if (!ev.runId) continue
      if (!seen.has(ev.runId)) {
        seen.add(ev.runId)
        list.push({ runId: ev.runId, firstSeen: ev.eventTimestamp.getTime(), lastSeen: ev.eventTimestamp.getTime() })
      } else {
        const r = list.find(r => r.runId === ev.runId)!
        r.lastSeen = ev.eventTimestamp.getTime()
      }
    }
    return list
  }, [sortedEvents])

  // ---------- panel y-axis ranges: auto-scale to actual data in view ----------
  // For continuous series, compute min/max within the current view, expand by
  // 10% headroom on each side. Fall back to a sensible default if no data.
  const ranges = useMemo(() => {
    // pct>0 scales to the [pct, 1-pct] percentile range (symmetric — both low
    // and high spikes excluded) instead of raw min/max, so a spike doesn't
    // flatten the signal. pct=0 keeps raw min/max. Falls back to min/max when
    // there are too few points for percentiles to be meaningful.
    function bounds(arrays: Pt[][], defaultMin: number, defaultMax: number, pct = 0): { min: number; max: number; ticks: number[] } {
      let lo = Infinity, hi = -Infinity
      const vals: number[] = []
      for (const arr of arrays) {
        for (const p of arr) {
          if (p.v == null) continue
          if (p.t < viewStart || p.t > viewEnd) continue
          if (p.v < lo) lo = p.v
          if (p.v > hi) hi = p.v
          if (pct > 0) vals.push(p.v)
        }
      }
      if (!Number.isFinite(lo)) return { min: defaultMin, max: defaultMax, ticks: niceTicks(defaultMin, defaultMax) }
      if (pct > 0 && vals.length >= 20) {
        vals.sort((a, b) => a - b)
        const at = (q: number) => vals[Math.min(vals.length - 1, Math.max(0, Math.round(q * (vals.length - 1))))]
        lo = at(pct)
        hi = at(1 - pct)
      }
      const pad = (hi - lo || Math.abs(hi) || 1) * 0.1
      const min = lo - pad
      const max = hi + pad
      return { min, max, ticks: niceTicks(min, max) }
    }
    return {
      weight: bounds([series.weight, series.weightS], -100, 1700, outlierPct['weight'] ?? 0),
      acc:    bounds([series.accX, series.accY, series.accZ], -4, 12),
      gyr:    bounds([series.gyrX, series.gyrY, series.gyrZ], -3, 3),
      sys:    { min: 0, max: 100, ticks: [0, 50, 100] },   // % is bounded
      cellWeight: bounds(series.cellWeight, -200, 1000, outlierPct['cells-weight'] ?? 0),
      cellRaw:    bounds(series.cellRaw, 0, 20000, outlierPct['cells-raw'] ?? 0),
    }
  }, [series, viewStart, viewEnd, outlierPct])

  // ---------- counts for the legend ----------
  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const ev of sortedEvents) c[ev.eventType] = (c[ev.eventType] || 0) + 1
    return c
  }, [sortedEvents])

  function toggleType(t: string) {
    setHidden(prev => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }

  function setPanelOutlierPct(key: string, pct: number) {
    setOutlierPct(prev => ({ ...prev, [key]: pct }))
  }

  // Sampling info per event type — drives the per-panel "N / M" indicator.
  // Returns null when the response didn't reduce that type.
  const sampledOf = useCallback(
    (eventType: string): { shown: number; total: number } | null => {
      const total = totalsPerType[eventType]
      if (total == null) return null
      const shown = events.reduce((c, ev) => ev.eventType === eventType ? c + 1 : c, 0)
      return total > shown ? { shown, total } : null
    },
    [totalsPerType, events],
  )

  // ---------- crosshair tracking ----------
  // Tooltip is panel-scoped: only show readouts for the panel currently under
  // the cursor. Lets the user hover individual load-cell panels and see those
  // values without drowning the tooltip in 11 unrelated rows.
  const [hoverPanel, setHoverPanel] = useState<string | null>(null)
  const onPanelsMouseMove = useCallback((e: React.MouseEvent) => {
    if (!panelsCanvasRef.current) return
    const target = e.target as HTMLElement
    if (target.closest('[data-event-glyph]')) {
      setHoverX(null); setHoverScreen(null); setHoverPanel(null)
      return
    }
    const rect = panelsCanvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (x < 0 || x > rect.width) { setHoverX(null); setHoverScreen(null); setHoverPanel(null); return }
    const panel = target.closest('[data-panel]') as HTMLElement | null
    setHoverPanel(panel?.dataset.panel ?? null)
    setHoverX(x)
    setHoverScreen({ x: e.clientX, y: e.clientY })
  }, [])
  const onPanelsMouseLeave = useCallback(() => { setHoverX(null); setHoverScreen(null); setHoverPanel(null) }, [])

  const hoverTime = hoverX != null && canvasW > 0
    ? viewStart + (hoverX / canvasW) * (viewEnd - viewStart)
    : null
  const [hoverScreen, setHoverScreen] = useState<{ x: number; y: number } | null>(null)


  // ---------- drag-to-zoom on panels ----------
  const [zoomDrag, setZoomDrag] = useState<{ startX: number; currX: number } | null>(null)
  const onPanelsMouseDown = useCallback((e: React.MouseEvent) => {
    if (!panelsCanvasRef.current) return
    if ((e.target as HTMLElement).closest('[data-event-glyph]')) return
    const rect = panelsCanvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    if (x < 0 || x > rect.width) return
    setZoomDrag({ startX: x, currX: x })
    e.preventDefault()
  }, [])
  useEffect(() => {
    if (!zoomDrag) return
    const onMove = (e: MouseEvent) => {
      if (!panelsCanvasRef.current) return
      const rect = panelsCanvasRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
      setZoomDrag(d => d ? { ...d, currX: x } : d)
    }
    const onUp = () => {
      setZoomDrag(d => {
        if (!d || !panelsCanvasRef.current) return null
        const x1 = Math.min(d.startX, d.currX)
        const x2 = Math.max(d.startX, d.currX)
        if (x2 - x1 > 4 && canvasW > 0) {
          const span = viewEnd - viewStart
          const newStart = viewStart + (x1 / canvasW) * span
          const newEnd   = viewStart + (x2 / canvasW) * span
          setView({ start: newStart, end: newEnd })
        }
        return null
      })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [zoomDrag, canvasW, viewStart, viewEnd, setView])

  // ---------- channel discovery ----------
  const nChannels = series.cellWeight.length
  // Channels visible by default — they're a primary diagnostic signal.
  const [showChannels, setShowChannels] = useState(true)

  return (
    <div className="h-screen flex flex-col bg-stone-50 font-mono text-stone-900 overflow-hidden">

      {/* HEADER */}
      <header className="flex-shrink-0 h-14 flex items-stretch border-b border-stone-200 bg-white">
        <div className="flex items-center gap-3 px-5 border-r border-stone-200 min-w-[300px]">
          <button onClick={() => onNavigate('#status')}
            className="flex items-center gap-2.5 cursor-pointer" title="home">
            <img src="/bear_icon_transparent.svg" alt="" className="h-9 w-auto" />
            <h1 className="font-display font-black text-lg tracking-widest text-bear uppercase leading-none hidden md:block">
              Kodiak
            </h1>
          </button>
          <div className="leading-tight ml-1">
            <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 font-medium flex items-center">
              <span>nets</span>
              <span className="text-stone-300 mx-1.5">/</span>
              <select
                value={hostname}
                onChange={(e) => {
                  const next = e.target.value
                  if (!next || next === hostname) return
                  onNavigate(`#device/${encodeURIComponent(next)}/timeline`)
                }}
                className="select-dark bg-transparent text-ink-700 tracking-[0.18em] text-[10px] font-medium uppercase border-none outline-none cursor-pointer hover:text-bear transition-colors"
                title="switch device"
              >
                {!allHostnames.includes(hostname) && <option value={hostname}>{hostname}</option>}
                {allHostnames.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center gap-1.5 px-4 overflow-x-auto">
          {!burstCaptureId && <>
          <NavButton title="back ½ window" onClick={() => panBy(-0.5)}>
            <path d="M9 3 L 4 8 L 9 13" />
          </NavButton>
          <NavButton title="zoom out 2×" onClick={() => zoomBy(2)}>
            <circle cx="7" cy="7" r="4" />
            <path d="M5 7 H 9" strokeLinecap="round" />
            <path d="M10.5 10.5 L 13.5 13.5" strokeLinecap="round" />
          </NavButton>
          <NavButton title="zoom in 2×" onClick={() => zoomBy(0.5)}>
            <circle cx="7" cy="7" r="4" />
            <path d="M7 5 V 9 M5 7 H 9" strokeLinecap="round" />
            <path d="M10.5 10.5 L 13.5 13.5" strokeLinecap="round" />
          </NavButton>
          <NavButton title="forward ½ window" onClick={() => panBy(0.5)}>
            <path d="M7 3 L 12 8 L 7 13" />
          </NavButton>
          <div className="w-2" />
          <RunPicker
            runs={runs}
            currentRunId={currentRunId}
            onSelect={selectRun}
            onStep={stepRun}
            onSetIntent={async (runId, intent) => {
              const r = await Devices.setRunIntent({ setRunIntentRequest: { runId, intent } })
              await refetchRuns()
              return r.notifiedCaptureIds.length
            }}
            tz={tz}
          />
          <RangePill ref={customAnchorRef} label="range custom" active={customOpen} onClick={() => setCustomOpen(o => !o)} />
          {customOpen && customAnchorRef.current && (
            <CustomRangePopover
              anchor={customAnchorRef.current}
              viewStart={viewStart} viewEnd={viewEnd} tz={tz}
              onApply={(s, e) => { setView({ start: s, end: e }); setCustomOpen(false) }}
              onClose={() => setCustomOpen(false)}
            />
          )}
          <button
            onClick={() => (live ? setLive(false) : enterLive())}
            title={live
              ? 'live: following now (last hour) — click to stop'
              : 'live: jump to the last hour and follow now'}
            className={`flex items-center gap-1.5 px-2.5 h-7 rounded text-[10px] tracking-[0.18em] uppercase font-medium border transition-colors cursor-pointer ${
              live
                ? 'bg-bear text-white border-bear'
                : 'bg-transparent text-stone-500 border-stone-200 hover:text-bear hover:border-bear/40'
            }`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${live ? 'bg-white animate-pulse' : 'bg-stone-400'}`} />
            live
          </button>
          </>}
          <div className="flex-1" />
          <button onClick={onLogout} className="text-[10px] tracking-[0.18em] uppercase text-stone-400 hover:text-bear transition-colors mr-3">
            log out
          </button>
        </div>
      </header>

      {/* Loading strip — sits between header and panels so it doesn't hide
          behind the brand wordmark. The 3px row is always present so the
          time axis below doesn't jump when a fetch starts/finishes; only
          the bar's color/animation toggles. */}
      <div
        className={`flex-shrink-0 h-[3px] w-full ${
          loading ? 'bg-bear/70 animate-pulse' : ''
        }`}
      />

      {burstCaptureId && (
        <BurstView
          hostname={hostname}
          captureId={burstCaptureId}
          onOpenBurst={handleOpenBurst}
          captureSummaries={captures}
          units={units}
          onUnits={setUnits}
          fmtClock={(ms) => fmtTime(ms, tz)}
          tzLabel={tzLabel(tz)}
          onOpenCatch={handleOpenCatch}
          onLabelsChange={(captureId, labels) => setCaptures(cats => cats.map(x => x.captureId === captureId ? { ...x, labels } : x))}
          onExit={(range) => {
            setBurstCaptureId(null)
            if (range) setView(range)
          }}
        />
      )}

      {/* CONTENT: panels + rail */}
      <div className={`flex-1 flex min-h-0 ${burstCaptureId ? 'hidden' : ''}`}>
        <div className="flex-1 flex flex-col border-r border-stone-200 bg-white min-w-0" ref={panelsRef}>

          {/* Time axis (drag to pan) */}
          <TimeAxis
            viewStart={viewStart} viewEnd={viewEnd} tz={tz} canvasW={canvasW}
            onSetView={(s, e) => setView({ start: s, end: e })}
            onSetTz={setTz}
          />

          {/* GPS lane */}
          <GpsLane gpsFixes={gpsFixes} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW}
                   onHover={setHoverEvent} onPin={setPinnedEvent} />

          {/* Event lanes */}
          <EventLanes
            byLane={discreteByLane}
            captureSpans={captureSpans}
            viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW}
            tz={tz} onOpenCatch={handleOpenCatch}
            onHover={setHoverEvent} onPin={setPinnedEvent}
            focusedCaptureId={focusedCaptureId}
          />

          {/* Scrollable panel area. Panels are the base layer; overlays
              (crosshair, zoom rect, lifecycle dashed lines) live on top in
              the canvas-only column (pointer-events: none). */}
          <div className="relative flex-1 min-h-0">
            <div
              className="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden"
              onMouseMove={onPanelsMouseMove}
              onMouseLeave={onPanelsMouseLeave}
              onMouseDown={onPanelsMouseDown}
            >
              <Panel
                name="Weight" unit={units === 'lb' ? 'pounds' : 'grams'} height={HEIGHTS.weight}
                panelKey="weight"
                outlierControl outlierPct={outlierPct['weight']} onSetOutlierPct={(p) => setPanelOutlierPct('weight', p)}
                yTicks={ranges.weight.ticks.map(t => convertGramsForAxis(t, units))}
                vMin={convertGramsForAxis(ranges.weight.min, units)}
                vMax={convertGramsForAxis(ranges.weight.max, units)}
                yTickFmt={(v) => units === 'lb' ? v.toFixed(2) : v.toFixed(0)}
                samplingInfo={sampledOf('weight_read')}
              >
                <PathTrace data={series.weight}        viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.weight} vMin={ranges.weight.min} vMax={ranges.weight.max}
                           color={TRACE_COLORS.weight} hidden={hidden.has('cont:weight')} strokeWidth={1.6} />
                <PathTrace data={series.weightS}       viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.weight} vMin={ranges.weight.min} vMax={ranges.weight.max}
                           color={TRACE_COLORS.weightSmoothed} hidden={hidden.has('cont:weight-s')} strokeWidth={1.1} opacity={0.85} />
              </Panel>

              {nChannels > 0 && showChannels && (
                <>
                  <Panel
                    name="Cells · weight" unit={`${nChannels} channels · grams`} height={HEIGHTS.cells}
                    panelKey="cells-weight"
                    outlierControl outlierPct={outlierPct['cells-weight']} onSetOutlierPct={(p) => setPanelOutlierPct('cells-weight', p)}
                    yTicks={ranges.cellWeight.ticks} vMin={ranges.cellWeight.min} vMax={ranges.cellWeight.max}
                  >
                    {series.cellWeight.map((arr, i) => (
                      <PathTrace key={i} data={arr} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.cells}
                                 vMin={ranges.cellWeight.min} vMax={ranges.cellWeight.max}
                                 color={channelColor(i, 'weight')} hidden={hidden.has(`cont:cell-w-${i}`)} strokeWidth={1.2} />
                    ))}
                  </Panel>
                  <Panel
                    name="Cells · raw" unit={`${nChannels} channels · ADC`} height={HEIGHTS.cells}
                    panelKey="cells-raw"
                    outlierControl outlierPct={outlierPct['cells-raw']} onSetOutlierPct={(p) => setPanelOutlierPct('cells-raw', p)}
                    yTicks={ranges.cellRaw.ticks} vMin={ranges.cellRaw.min} vMax={ranges.cellRaw.max}
                  >
                    {series.cellRaw.map((arr, i) => (
                      <PathTrace key={i} data={arr} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.cells}
                                 vMin={ranges.cellRaw.min} vMax={ranges.cellRaw.max}
                                 color={channelColor(i, 'raw')} hidden={hidden.has(`cont:cell-r-${i}`)} strokeWidth={1.2} />
                    ))}
                  </Panel>
                </>
              )}

              <Panel
                name="IMU · Acc" unit="m/s²" height={HEIGHTS.acc}
                panelKey="acc"
                yTicks={ranges.acc.ticks} vMin={ranges.acc.min} vMax={ranges.acc.max}
                samplingInfo={sampledOf('imu_read')}
              >
                <PathTrace data={series.accX} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.acc} vMin={ranges.acc.min} vMax={ranges.acc.max} color={TRACE_COLORS.accX} hidden={hidden.has('cont:acc-x')} />
                <PathTrace data={series.accY} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.acc} vMin={ranges.acc.min} vMax={ranges.acc.max} color={TRACE_COLORS.accY} hidden={hidden.has('cont:acc-y')} />
                <PathTrace data={series.accZ} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.acc} vMin={ranges.acc.min} vMax={ranges.acc.max} color={TRACE_COLORS.accZ} hidden={hidden.has('cont:acc-z')} />
              </Panel>

              <Panel
                name="IMU · Gyr" unit="rad/s" height={HEIGHTS.gyr}
                panelKey="gyr"
                yTicks={ranges.gyr.ticks} vMin={ranges.gyr.min} vMax={ranges.gyr.max}
                samplingInfo={sampledOf('imu_read')}
              >
                <PathTrace data={series.gyrX} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.gyr} vMin={ranges.gyr.min} vMax={ranges.gyr.max} color={TRACE_COLORS.gyrX} hidden={hidden.has('cont:gyr-x')} />
                <PathTrace data={series.gyrY} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.gyr} vMin={ranges.gyr.min} vMax={ranges.gyr.max} color={TRACE_COLORS.gyrY} hidden={hidden.has('cont:gyr-y')} />
                <PathTrace data={series.gyrZ} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.gyr} vMin={ranges.gyr.min} vMax={ranges.gyr.max} color={TRACE_COLORS.gyrZ} hidden={hidden.has('cont:gyr-z')} />
              </Panel>

              <Panel
                name="Sysinfo" unit="%" height={HEIGHTS.sysinfo}
                panelKey="sys"
                yTicks={ranges.sys.ticks} vMin={ranges.sys.min} vMax={ranges.sys.max}
                samplingInfo={sampledOf('sysinfo')}
              >
                <PathTrace data={series.cpu}  viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.sysinfo} vMin={ranges.sys.min} vMax={ranges.sys.max} color={TRACE_COLORS.cpu}  hidden={hidden.has('cont:cpu')}  strokeWidth={1.1} opacity={0.85} />
                <PathTrace data={series.mem}  viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.sysinfo} vMin={ranges.sys.min} vMax={ranges.sys.max} color={TRACE_COLORS.mem}  hidden={hidden.has('cont:mem')}  strokeWidth={1.1} opacity={0.85} />
                <PathTrace data={series.swap} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} h={HEIGHTS.sysinfo} vMin={ranges.sys.min} vMax={ranges.sys.max} color={TRACE_COLORS.swap} hidden={hidden.has('cont:swap')} strokeWidth={1.1} opacity={0.85} />
              </Panel>
            </div>

            {/* Overlay layer: crosshair, zoom rect, lifecycle dashed lines.
                Positioned over the canvas area only (offset by LABEL_W).
                pointer-events: none so panels still receive mouse events. */}
            <div ref={panelsCanvasRef}
                 className="absolute top-0 bottom-0 pointer-events-none overflow-hidden"
                 style={{ left: LABEL_W, right: 0 }}>
              {hoverX != null && (
                <div className="absolute top-0 bottom-0 w-px bg-ink-200/70 z-10"
                     style={{ left: hoverX }} />
              )}
              {zoomDrag && (
                <div className="absolute top-0 bottom-0 border-l border-r border-bear bg-bear/5 z-20"
                     style={{ left: Math.min(zoomDrag.startX, zoomDrag.currX),
                              width: Math.abs(zoomDrag.currX - zoomDrag.startX) }} />
              )}
              <LifecycleLines
                events={discreteByLane.lifecycle}
                captureSpans={captureSpans}
                viewStart={viewStart} viewEnd={viewEnd}
                canvasW={canvasW}
                hidden={hidden}
              />
            </div>
          </div>
        </div>

        {/* RIGHT RAIL — collapsible drawer. */}
        <aside
          className="flex-shrink-0 bg-white border-l border-stone-200 transition-[width] duration-200 ease-out relative"
          style={{ width: railOpen ? RAIL_W : 32 }}
        >
          <button
            onClick={() => setRailOpen(o => !o)}
            title={railOpen ? 'collapse' : 'expand'}
            className="absolute top-3 left-1.5 w-7 h-7 grid place-items-center text-stone-400 hover:text-bear hover:bg-stone-50 rounded transition-colors z-10"
          >
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              {railOpen
                ? <path d="M11 3 L 5 8 L 11 13" strokeLinecap="round" strokeLinejoin="round"/>
                : <path d="M5 3 L 11 8 L 5 13" strokeLinecap="round" strokeLinejoin="round"/>}
            </svg>
          </button>
          {railOpen && (
            <div className="px-5 py-5 pl-12 space-y-7 overflow-y-auto h-full">
              <RailSection title="Weight units">
                <div className="flex gap-1">
                  {(['g', 'lb'] as const).map(u => (
                    <button key={u} onClick={() => setUnits(u)}
                      className={`h-7 px-3 rounded-md border text-[11px] font-medium transition-colors
                        ${units === u
                          ? 'bg-bear/5 text-bear-dark border-bear/30'
                          : 'bg-white text-ink-500 border-stone-200 hover:border-ink-200'}`}>
                      {u === 'g' ? 'grams' : 'pounds'}
                    </button>
                  ))}
                </div>
              </RailSection>
              <RailSection title="Continuous">
                <ContinuousLegend hidden={hidden} toggle={(k) => toggleType(`cont:${k}`)} hasChannels={nChannels > 0} showChannels={showChannels} setShowChannels={setShowChannels} nChannels={nChannels} />
              </RailSection>
              <RailSection title="Discrete">
                <DiscreteLegend hidden={hidden} toggle={toggleType} counts={counts} />
              </RailSection>
              <RailSection title="Runs in window">
                <RunsList runs={runsInView} tz={tz} onClick={(r) => {
                  const dur = Math.max(r.lastSeen - r.firstSeen, 1000)
                  const pad = Math.max(5000, dur * 0.1)
                  setView({ start: r.firstSeen - pad, end: r.lastSeen + pad })
                }} />
              </RailSection>
              <RailSection title="Window totals">
                <Stats events={sortedEvents} />
              </RailSection>
            </div>
          )}
        </aside>
      </div>

      <EventTooltip
        data={visibleEvent} tz={tz}
        pinned={pinnedEvent !== null}
        onClose={() => setPinnedEvent(null)}
        onOpenCatch={handleOpenCatch}
        onOpenBurst={handleOpenBurst}
      />

      {/* Cursor readout follows the mouse over the panel canvases. Hidden
          when an event tooltip is showing so the two don't pile up. */}
      {hoverScreen && hoverTime != null && hoverPanel && !visibleEvent && (
        <TraceTooltip x={hoverScreen.x} y={hoverScreen.y} t={hoverTime} tz={tz} units={units} series={series} panel={hoverPanel} />
      )}

      {/* Catch detail modal — opened from a pinned capture tooltip. */}
      {openedCapture && (
        <CaptureSidePanel
          captureItem={openedCapture}
          detail={openedDetail}
          onClose={() => setOpenCaptureId(null)}
          onTriageChange={handleTriageChange}
          onAnnotationSave={handleAnnotationSave}
          onMediaUploaded={handleMediaUploaded}
          onLinked={handleLinked}
          onLabelsChange={(captureId, labels) => setCaptures(cats => cats.map(x => x.captureId === captureId ? { ...x, labels } : x))}
          open={!!openedCapture}
          suppressTimelineLink={true}
          onOpenBurst={handleOpenBurst}
          onPrev={handlePrev}
          onNext={handleNext}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
        />
      )}
      {openCaptureId && !openedCapture && (
        <div className="fixed inset-0 bg-black/20 z-40 grid place-items-center"
             onClick={() => setOpenCaptureId(null)}>
          <div className="bg-white rounded-lg border border-stone-200 px-5 py-4 shadow-lg max-w-md"
               onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-extrabold text-[12px] uppercase tracking-[0.18em] text-ink-700 mb-2">
              Catch not found
            </div>
            <div className="text-[11px] text-stone-500 mb-3">
              No capture on the server matches id <span className="font-mono text-ink-700">{openCaptureId}</span>.
              The capture may not have finished ingesting yet.
            </div>
            <button onClick={() => setOpenCaptureId(null)}
                    className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500 hover:text-ink-700 px-3 py-1">
              dismiss
            </button>
          </div>
        </div>
      )}

      {/* STATUS BAR */}
      <footer className="h-8 flex-shrink-0 flex items-center px-5 gap-4 border-t border-stone-200 bg-white text-[10px] text-stone-500">
        <span className="text-ink-700 font-medium">{hostname}</span>
        <span className="text-stone-300">·</span>
        <span>{events.length.toLocaleString()} events in window</span>
        <span className="text-stone-300">·</span>
        <span>{loading ? 'loading…' : error ? <span className="text-bear">error: {error}</span> : 'ready'}</span>
        <span className="flex-1" />
        {hoverTime != null && <span className="text-ink-500">cursor · {fmtFull(hoverTime, tz)}</span>}
      </footer>
    </div>
  )
}

// ============================================================
// Sub-components
// ============================================================

function CustomRangePopover({ anchor, viewStart, viewEnd, tz, onApply, onClose }: {
  anchor: HTMLElement
  viewStart: number; viewEnd: number; tz: Tz
  onApply: (start: number, end: number) => void
  onClose: () => void
}) {
  const [from, setFrom] = useState(() => dtLocalString(viewStart, tz))
  const [to,   setTo]   = useState(() => dtLocalString(viewEnd, tz))
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)
  // Position fixed below the anchor button (escapes the header's overflow clip).
  const rect = anchor.getBoundingClientRect()
  const POPOVER_W = 300
  const left = Math.max(8, Math.min(window.innerWidth - POPOVER_W - 8, rect.right - POPOVER_W))
  const top  = rect.bottom + 6

  // Click-outside to close.
  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onClose()
    }
    // Defer one tick so the same click that opened doesn't close it.
    const id = setTimeout(() => document.addEventListener('mousedown', onDocDown), 0)
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDocDown) }
  }, [onClose])

  function apply() {
    const s = parseDtLocal(from, tz)
    const e = parseDtLocal(to, tz)
    if (!Number.isFinite(s) || !Number.isFinite(e)) { setError('invalid date'); return }
    if (e <= s) { setError('"to" must be after "from"'); return }
    onApply(s, e)
  }

  return (
    <div ref={ref}
         style={{ position: 'fixed', left, top, width: POPOVER_W }}
         className="z-50 bg-white border border-stone-300 rounded-lg shadow-lg p-4"
         onClick={(e) => e.stopPropagation()}>
      <div className="font-display font-extrabold text-[11px] uppercase tracking-[0.18em] text-ink-700 mb-3">Custom range</div>
      <div className="space-y-2.5">
        <div>
          <label className="block text-[9.5px] tracking-[0.16em] uppercase text-stone-500 font-semibold mb-1">From ({tzLabel(tz)})</label>
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="w-full font-mono text-[12px] px-2 py-1.5 border border-stone-200 rounded text-ink-700 focus:outline-none focus:border-bear/50" />
        </div>
        <div>
          <label className="block text-[9.5px] tracking-[0.16em] uppercase text-stone-500 font-semibold mb-1">To ({tzLabel(tz)})</label>
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)}
                 className="w-full font-mono text-[12px] px-2 py-1.5 border border-stone-200 rounded text-ink-700 focus:outline-none focus:border-bear/50" />
        </div>
        {error && <div className="text-[10px] text-bear">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose}
                  className="font-mono text-[10px] tracking-[0.18em] uppercase text-stone-500 hover:text-ink-700 px-2 py-1">cancel</button>
          <button onClick={apply}
                  className="font-mono text-[10px] tracking-[0.18em] uppercase text-bear hover:text-bear-light px-3 py-1 border border-bear/30 rounded bg-bear/5 hover:bg-bear/10 transition-colors">apply</button>
        </div>
      </div>
    </div>
  )
}

function NavButton({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className="h-7 w-7 grid place-items-center rounded-md border border-stone-200 bg-white text-ink-300 hover:text-bear hover:border-bear/30 transition-colors"
    >
      <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
        {children}
      </svg>
    </button>
  )
}

// Small session_intent badge for the run picker — fishing/testing get a
// colored pill; unknown (no session_intent event recorded) renders nothing
// so unlabeled historical runs don't clutter the picker. A team override
// (set from the intent menu) shows a dashed outline so it reads as "edited".
function SessionIntentBadge({ intent, overridden }: { intent: SessionIntent | undefined; overridden?: boolean }) {
  const edited = overridden ? ' border border-dashed border-current' : ''
  const title = overridden ? 'set by the team (overrides the device answer)' : undefined
  if (intent === SessionIntent.Fishing) {
    return <span title={title} className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded bg-pine/10 text-pine${edited}`}>fishing</span>
  }
  if (intent === SessionIntent.Testing) {
    return <span title={title} className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded bg-gold/20 text-[#8a6a1f]${edited}`}>testing</span>
  }
  return null
}

// The selected run's intent label, attached to the right end of the run pill
// and clickable: overrides the run's fishing/testing intent (or clears the
// override). The menu header names the run so it's unambiguous what the
// change applies to even when the view is zoomed out. Flipping a run to
// fishing also posts its already-eligible captures to the team feed; we
// surface the count briefly so the operator knows it happened.
function RunIntentMenu({ run, label, onSetIntent }: {
  run: RunSummary | undefined
  label: string
  onSetIntent: (runId: string, intent: SessionIntent | null) => Promise<number>
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDown) }
  }, [open])
  if (!run) return null
  const apply = async (intent: SessionIntent | null) => {
    setBusy(true); setOpen(false)
    try {
      const posted = await onSetIntent(run.runId, intent)
      setNote(intent === null ? 'override cleared' : posted > 0 ? `posted ${posted} to feed` : 'saved')
    } catch {
      setNote('failed')
    } finally {
      setBusy(false)
      setTimeout(() => setNote(null), 4000)
    }
  }
  const item = 'w-full text-left px-3 py-1.5 text-[11px] hover:bg-stone-50 disabled:opacity-40'
  const overridden = run.intentOverride != null
  const intent = run.sessionIntent
  const tone =
    intent === SessionIntent.Fishing ? 'bg-pine/10 text-pine border-pine/30'
    : intent === SessionIntent.Testing ? 'bg-gold/20 text-[#8a6a1f] border-gold/40'
    : 'bg-white text-stone-400 border-stone-200'
  const text = intent === SessionIntent.Fishing ? 'fishing' : intent === SessionIntent.Testing ? 'testing' : 'unlabeled'
  return (
    <div ref={ref} className="relative flex items-center gap-2">
      <button
        ref={anchorRef}
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        title={`${label} run · ${overridden ? 'team override' : 'device answer'}: ${text} — click to change`}
        className={`h-7 px-2 rounded-r-md border border-l-0 text-[10px] font-medium flex items-center gap-1 hover:brightness-95 disabled:opacity-50 ${tone}`}
      >
        <span className={overridden ? 'underline decoration-dashed underline-offset-2' : ''}>{text}</span>
        <span className="text-[9px]">▾</span>
      </button>
      {note && <span className="text-[10px] text-stone-500">{note}</span>}
      {open && anchorRef.current && (
        // Fixed + anchored to the button's screen rect: the toolbar clips
        // overflow, so an absolutely positioned child would be cut off.
        <div
          className="fixed z-40 w-max bg-white border border-stone-200 rounded-md shadow-lg py-1"
          style={{
            top: anchorRef.current.getBoundingClientRect().bottom + 4,
            left: anchorRef.current.getBoundingClientRect().left,
          }}
        >
          <div className="px-3 py-1.5 text-[10px] text-stone-500 border-b border-stone-100">
            <span className="font-mono text-ink-700">{label}</span> run · {overridden ? 'team override' : 'device answer'}: {text}
          </div>
          <button className={item} disabled={run.intentOverride === SessionIntent.Fishing} onClick={() => apply(SessionIntent.Fishing)}>mark as fishing</button>
          <button className={item} disabled={run.intentOverride === SessionIntent.Testing} onClick={() => apply(SessionIntent.Testing)}>mark as testing</button>
          <button className={item} disabled={run.intentOverride == null} onClick={() => apply(null)}>clear override (use device answer)</button>
        </div>
      )}
    </div>
  )
}

function RunPicker({
  runs, currentRunId, onSelect, onStep, onSetIntent, tz,
}: {
  runs: RunSummary[]
  currentRunId: string | null
  onSelect: (runId: string) => void
  onStep: (direction: -1 | 1) => void
  onSetIntent: (runId: string, intent: SessionIntent | null) => Promise<number>
  tz: Tz
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const current = runs.find(r => r.runId === currentRunId)
  const currentIdx = current ? runs.findIndex(r => r.runId === currentRunId) : -1
  // runs are DESC: 0=newest. Newer step = lower index. Older step = higher index.
  const hasNewer = currentIdx > 0
  const hasOlder = currentIdx >= 0 && currentIdx < runs.length - 1

  // Click-outside-popover → close.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (popoverRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      setOpen(false)
    }
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDown) }
  }, [open])

  const currentLabelTs = current
    ? (current.startedAt != null
        ? current.startedAt * 1000
        : current.earliestEventTimestamp?.getTime() ?? null)
    : null
  const label = currentLabelTs != null
    ? `${fmtDate(currentLabelTs, tz)} ${fmtTimeShort(currentLabelTs, tz)}`
    : current
      ? current.runId
      : runs.length === 0 ? 'no runs' : 'select run'

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onStep(-1)}
        disabled={!hasOlder}
        className="h-7 w-7 grid place-items-center rounded-md border border-stone-200 text-ink-500 hover:border-ink-200 disabled:opacity-30 disabled:cursor-not-allowed"
        title="previous (older) run"
      >
        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M9 3 L 4 8 L 9 13" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <button
        ref={anchorRef}
        onClick={() => setOpen(o => !o)}
        disabled={runs.length === 0}
        className={`h-7 px-3 border text-[11px] font-medium transition-colors flex items-center gap-2
          ${current ? 'rounded-l-md' : 'rounded-md'}
          ${current
            ? 'bg-bear/5 text-bear-dark border-bear/30'
            : 'bg-white text-ink-500 border-stone-200 hover:border-ink-200'} disabled:opacity-50`}
      >
        <span className={`text-[9.5px] tracking-[0.18em] uppercase ${current ? 'text-bear' : 'text-stone-400'}`}>run</span>
        <span>{label}</span>
        {current && (current.eventCount ?? 0) > 0 && (
          <span className="text-[9.5px] text-stone-500">{(current.eventCount ?? 0).toLocaleString()} ev</span>
        )}
      </button>
      <RunIntentMenu run={current} label={label} onSetIntent={onSetIntent} />
      <button
        onClick={() => onStep(1)}
        disabled={!hasNewer}
        className="h-7 w-7 grid place-items-center rounded-md border border-stone-200 text-ink-500 hover:border-ink-200 disabled:opacity-30 disabled:cursor-not-allowed"
        title="next (newer) run"
      >
        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M7 3 L 12 8 L 7 13" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && anchorRef.current && (
        <div
          ref={popoverRef}
          className="absolute z-40 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-[400px] overflow-y-auto"
          style={{
            top: anchorRef.current.getBoundingClientRect().bottom + 4,
            left: anchorRef.current.getBoundingClientRect().left,
            minWidth: 280,
          }}
        >
          {runs.map(r => {
            // Prefer Run.started_at, but fall back to the earliest event ts —
            // some runs land with started_at unset and we don't want a raw
            // run_id slug in the dropdown.
            const labelTs =
              r.startedAt != null
                ? r.startedAt * 1000
                : r.earliestEventTimestamp?.getTime() ?? null
            const isActive = r.runId === currentRunId
            return (
              <button
                key={r.runId}
                onClick={() => { onSelect(r.runId); setOpen(false) }}
                className={`w-full px-3 py-2 text-left flex items-center justify-between gap-3 hover:bg-stone-50 border-b border-stone-100 last:border-b-0
                  ${isActive ? 'bg-bear/5' : ''}`}
              >
                <span className="font-mono text-[11px] text-ink-700 flex items-center gap-2">
                  {labelTs != null ? `${fmtDate(labelTs, tz)} ${fmtTime(labelTs, tz)}` : r.runId}
                  <SessionIntentBadge intent={r.sessionIntent} overridden={r.intentOverride != null} />
                </span>
                <span className="font-mono text-[10px] text-stone-400">
                  {(r.eventCount ?? 0).toLocaleString()} ev
                  {r.captureCount > 0 && <span className="text-pine ml-2">{r.captureCount} captures</span>}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// TzPicker — small dropdown anchored under the "time · X" label. UTC and
// Local cover the common cases; "Custom offset" lets the user dial in any
// fixed offset (e.g., view a BC-deployed net's data in Pacific time while
// sitting in NY). Stepper-style — one click per 15-min step; full IANA zone
// search is overkill for this use.
function TzPicker({ tz, onSet }: { tz: Tz; onSet: (tz: Tz) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (popRef.current?.contains(t) || ref.current?.contains(t)) return
      setOpen(false)
    }
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => { clearTimeout(id); document.removeEventListener('mousedown', onDown) }
  }, [open])

  const localOffsetMin = -new Date().getTimezoneOffset()
  const localOffsetLabel = formatOffset(localOffsetMin)

  const isCustom = typeof tz === 'object'
  const customOffset = isCustom ? tz.offsetMinutes : -new Date().getTimezoneOffset()
  const stepCustom = (delta: number) => {
    const next = Math.max(-12 * 60, Math.min(14 * 60, customOffset + delta))
    onSet({ offsetMinutes: next })
  }

  return (
    <>
      <button
        ref={ref}
        onClick={() => setOpen(o => !o)}
        className="text-left flex flex-col gap-0.5 hover:text-bear transition-colors cursor-pointer group"
        title="change timezone"
      >
        <span className="text-[9.5px] tracking-[0.20em] uppercase text-stone-400 font-semibold group-hover:text-bear">time</span>
        <span className="text-[10px] tracking-[0.16em] uppercase text-ink-700 font-semibold flex items-center gap-1">
          {tzLabel(tz)}
          <svg viewBox="0 0 8 5" className="w-2 h-1.5 text-stone-400 group-hover:text-bear" fill="currentColor"><path d="M0 0 L4 5 L8 0 Z"/></svg>
        </span>
      </button>
      {open && ref.current && (
        <div
          ref={popRef}
          className="absolute z-40 bg-white border border-stone-200 rounded-md shadow-lg overflow-hidden"
          style={{
            top: ref.current.getBoundingClientRect().bottom + 4,
            left: ref.current.getBoundingClientRect().left,
            minWidth: 220,
          }}
        >
          <TzOption
            label="UTC"
            sub="Coordinated Universal Time"
            active={tz === 'utc'}
            onClick={() => { onSet('utc'); setOpen(false) }}
          />
          <TzOption
            label="Local"
            sub={localOffsetLabel}
            active={tz === 'local'}
            onClick={() => { onSet('local'); setOpen(false) }}
          />
          <CustomOffsetRow
            active={isCustom}
            offsetMinutes={customOffset}
            onSelect={() => onSet({ offsetMinutes: customOffset })}
            onChange={(m) => onSet({ offsetMinutes: m })}
            onStep={stepCustom}
          />
        </div>
      )}
    </>
  )
}

// Editable custom-offset row. Lets the user click the +/- buttons OR type
// the offset directly (e.g. "−07:00", "-7", "+5:30"). Local state during
// edit so a half-typed string doesn't jump back to the parsed value.
function CustomOffsetRow({ active, offsetMinutes, onSelect, onChange, onStep }: {
  active: boolean
  offsetMinutes: number
  onSelect: () => void
  onChange: (m: number) => void
  onStep: (delta: number) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const display = formatOffset(offsetMinutes)
  const commit = () => {
    const m = parseOffset(draft)
    if (m != null) onChange(m)
    setEditing(false)
  }
  return (
    <div
      className={`px-3.5 py-2 flex items-center justify-between gap-3 border-b border-stone-100 last:border-b-0
        ${active ? 'bg-bear/5' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <button
          onClick={onSelect}
          className="font-mono text-[11px] text-ink-700 uppercase tracking-wider cursor-pointer hover:text-bear text-left block"
        >Custom</button>
        {editing ? (
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              else if (e.key === 'Escape') setEditing(false)
            }}
            className="w-20 font-mono text-[10px] text-ink-700 bg-white border border-bear/40 rounded px-1 py-0.5 focus:outline-none"
            placeholder="±HH:MM"
          />
        ) : (
          <button
            onClick={() => { setDraft(display.replace('UTC', '')); setEditing(true) }}
            className="font-mono text-[9.5px] text-stone-400 hover:text-bear cursor-text text-left block"
            title="click to type an offset"
          >{display}</button>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onStep(-15)}
          className="w-6 h-6 rounded border border-stone-200 text-stone-500 hover:text-bear hover:border-bear/40 font-mono text-[12px] leading-none flex items-center justify-center cursor-pointer"
          title="−15 min"
        >−</button>
        <button
          onClick={() => onStep(15)}
          className="w-6 h-6 rounded border border-stone-200 text-stone-500 hover:text-bear hover:border-bear/40 font-mono text-[12px] leading-none flex items-center justify-center cursor-pointer"
          title="+15 min"
        >+</button>
      </div>
    </div>
  )
}

// Parse user-typed offsets. Accepts "−HH:MM", "+H", "-7:30", "8", etc.
// Both ASCII "-" and Unicode minus "−" count as negative. Returns null if
// unparseable so the row falls back to the previous value silently.
function parseOffset(s: string): number | null {
  const trimmed = s.trim().replace(/^UTC/i, '').replace(/[−–—]/, '-')
  if (!trimmed) return null
  const m = /^([+-]?)(\d{1,2})(?::(\d{1,2}))?$/.exec(trimmed)
  if (!m) return null
  const sign = m[1] === '-' ? -1 : 1
  const h = parseInt(m[2], 10)
  const min = m[3] ? parseInt(m[3], 10) : 0
  if (h > 14 || min >= 60) return null
  const total = sign * (h * 60 + min)
  if (total < -12 * 60 || total > 14 * 60) return null
  return total
}

function formatOffset(min: number): string {
  const sign = min >= 0 ? '+' : '−'
  const abs = Math.abs(min)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

function TzOption({ label, sub, active, onClick }: {
  label: string; sub: string; active: boolean; onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full px-3.5 py-2 text-left flex items-center justify-between gap-3 hover:bg-stone-50 border-b border-stone-100 last:border-b-0
        ${active ? 'bg-bear/5' : ''}`}
    >
      <div>
        <div className="font-mono text-[11px] text-ink-700 uppercase tracking-wider">{label}</div>
        <div className="font-mono text-[9.5px] text-stone-400">{sub}</div>
      </div>
      {active && (
        <svg viewBox="0 0 12 12" className="w-3 h-3 text-bear" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 6 L 5 9 L 10 3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  )
}

const RangePill = React.forwardRef<HTMLButtonElement, { label: string; active: boolean; onClick: () => void }>(
  function RangePill({ label, active, onClick }, ref) {
    return (
      <button
        ref={ref}
        onClick={onClick}
        className={`h-7 px-3 rounded-md border text-[11px] font-medium transition-colors flex items-center gap-2
          ${active
            ? 'bg-bear/5 text-bear-dark border-bear/30'
            : 'bg-white text-ink-500 border-stone-200 hover:border-ink-200'}`}
      >
        <span className={`text-[9.5px] tracking-[0.18em] uppercase ${active ? 'text-bear' : 'text-stone-400'}`}>range</span>
        <span>{label}</span>
      </button>
    )
  }
)

function TimeAxis({ viewStart, viewEnd, tz, canvasW, onSetView, onSetTz }: {
  viewStart: number; viewEnd: number; tz: Tz; canvasW: number
  onSetView: (start: number, end: number) => void
  onSetTz: (tz: Tz) => void
}) {
  const dur = (viewEnd - viewStart) / 1000
  const ticks = useMemo(() => {
    const candidates = [10, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400, 172800]
    const step = (candidates.find(s => dur / s <= 12) || 86400) * 1000
    const startAligned = Math.ceil(viewStart / step) * step
    const out: { t: number; midnight: boolean }[] = []
    for (let t = startAligned; t <= viewEnd; t += step) {
      const p = tzParts(new Date(t), tz)
      out.push({ t, midnight: p.h === 0 && p.mi === 0 })
    }
    return out
  }, [viewStart, viewEnd, dur, tz])

  // Drag-to-pan: capture startX + view at mousedown, dispatch deltas on mousemove.
  const dragStartRef = useRef<{ x: number; t0Start: number; t0End: number } | null>(null)
  const onMouseDown = (e: React.MouseEvent) => {
    if (canvasW <= 0) return
    dragStartRef.current = { x: e.clientX, t0Start: viewStart, t0End: viewEnd }
    e.preventDefault()
  }
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragStartRef.current
      if (!d || canvasW <= 0) return
      const dx = e.clientX - d.x
      // Drag right (dx>0) → pull content right → see earlier times (negative dt).
      const dt = -(dx / canvasW) * (d.t0End - d.t0Start)
      onSetView(d.t0Start + dt, d.t0End + dt)
    }
    const onUp = () => { dragStartRef.current = null }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [canvasW, onSetView])

  return (
    <div className="h-12 flex items-stretch border-b border-stone-200 bg-stone-50/50 flex-shrink-0">
      <div className="flex-shrink-0 px-5 flex items-center border-r border-stone-200" style={{ width: LABEL_W }}>
        <TzPicker tz={tz} onSet={onSetTz} />
      </div>
      {/* overflow-hidden so the rightmost tick's date label can't bleed into
          the right rail. */}
      <div className="relative flex-1 cursor-ew-resize select-none overflow-hidden" onMouseDown={onMouseDown}>
        {canvasW > 0 && ticks.map(tk => {
          const x = ((tk.t - viewStart) / (viewEnd - viewStart)) * canvasW
          return (
            <div key={tk.t}
                 className={`absolute top-0 bottom-0 border-l pl-1.5 pt-1.5 pointer-events-none whitespace-nowrap
                   ${tk.midnight ? 'border-stone-300' : 'border-stone-200'}`}
                 style={{ left: x }}>
              <div className={`text-[9.5px] leading-none ${tk.midnight ? 'text-bear font-medium' : 'text-stone-400'}`}>
                {fmtDate(tk.t, tz)}
              </div>
              <div className={`text-[10px] leading-none mt-1 ${tk.midnight ? 'text-ink-700 font-medium' : 'text-ink-300'}`}>
                {fmtTimeShort(tk.t, tz)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function GpsLane({ gpsFixes, viewStart, viewEnd, canvasW, onHover, onPin }: {
  gpsFixes: DeviceEventSummary[]; viewStart: number; viewEnd: number; canvasW: number
  onHover: HoverSetter; onPin: PinSetter
}) {
  return (
    <div className="h-6 flex items-stretch border-b border-stone-200 bg-stone-50/50 flex-shrink-0">
      <div className="flex-shrink-0 px-5 flex items-center gap-2 border-r border-stone-200" style={{ width: LABEL_W }}>
        <span className="w-1.5 h-1.5 rounded-full bg-pine"/>
        <span className="text-[9.5px] tracking-[0.18em] uppercase text-stone-500 font-semibold">gps_fix</span>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full overflow-visible">
          {canvasW > 0 && gpsFixes.map(ev => {
            const t = ev.eventTimestamp.getTime()
            if (t < viewStart || t > viewEnd) return null
            const p = ev.payload as { hasFix: boolean }
            const x = tToX(t, viewStart, viewEnd, canvasW)
            return (
              <g key={ev.id} data-event-glyph
                 onClick={(e) => { e.stopPropagation(); onPin({ ev, x: e.clientX, y: e.clientY }) }}
                 onMouseEnter={(e) => onHover({ ev, x: e.clientX, y: e.clientY })}
                 onMouseMove={(e) => onHover({ ev, x: e.clientX, y: e.clientY })}
                 onMouseLeave={() => onHover(null)}
                 className="cursor-pointer">
                <circle cx={x} cy={12} r={p.hasFix ? 2.5 : 1.8}
                        fill={p.hasFix ? '#355E3B' : '#a8a29e'} />
                <circle cx={x} cy={12} r={6} fill="transparent" />
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

type HoverSetter = (h: { ev: DeviceEventSummary; x: number; y: number } | null) => void
type PinSetter = (h: { ev: DeviceEventSummary; x: number; y: number } | null) => void

function EventLanes({ byLane, captureSpans, viewStart, viewEnd, canvasW, tz, onOpenCatch, onHover, onPin, focusedCaptureId }: {
  byLane: Record<LaneKey, DeviceEventSummary[]>
  captureSpans: Record<string, { trigger?: DeviceEventSummary; complete?: DeviceEventSummary }>
  viewStart: number; viewEnd: number; canvasW: number; tz: Tz
  onOpenCatch: (id: string) => void
  onHover: HoverSetter; onPin: PinSetter
  focusedCaptureId?: string | null
}) {
  return (
    <div className="flex-shrink-0 border-b border-stone-200 bg-stone-50/50 flex flex-col">
      {LANE_KEYS.map(laneKey => (
        <div key={laneKey} className="flex border-b border-stone-100 last:border-b-0 h-[22px]">
          <div className="flex-shrink-0 px-5 flex items-center border-r border-stone-200" style={{ width: LABEL_W }}>
            <span className="text-[9.5px] tracking-[0.16em] uppercase text-stone-500 font-medium">{laneKey}</span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <svg className="absolute inset-0 w-full h-full overflow-visible">
              {laneKey === 'captures'
                ? <CapturesLaneContent spans={captureSpans} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} onOpenCatch={onOpenCatch} onHover={onHover} onPin={onPin} focusedCaptureId={focusedCaptureId} />
                : <SimpleLaneContent items={byLane[laneKey]} viewStart={viewStart} viewEnd={viewEnd} canvasW={canvasW} onHover={onHover} onPin={onPin} />
              }
            </svg>
          </div>
        </div>
      ))}
    </div>
  )
}

function CapturesLaneContent({ spans, viewStart, viewEnd, canvasW, onOpenCatch, onHover, onPin, focusedCaptureId }: {
  spans: Record<string, { trigger?: DeviceEventSummary; complete?: DeviceEventSummary }>
  viewStart: number; viewEnd: number; canvasW: number
  onOpenCatch: (id: string) => void; onHover: HoverSetter; onPin: PinSetter
  focusedCaptureId?: string | null
}) {
  if (canvasW <= 0) return null
  const yC = 11
  return (
    <>
      {Object.entries(spans).map(([cid, { trigger, complete }]) => {
        const isFocused = cid === focusedCaptureId
        const trigT = trigger?.eventTimestamp.getTime()
        const compT = complete?.eventTimestamp.getTime()
        if (trigT && compT) {
          const x1 = tToX(trigT, viewStart, viewEnd, canvasW)
          const x2 = tToX(compT, viewStart, viewEnd, canvasW)
          if (x2 < 0 || x1 > canvasW) return null
          const ok = (complete!.payload as { ok?: boolean }).ok !== false
          const color = ok ? '#355E3B' : '#9B3C3C'
          return (
            <g key={cid}>
              <line x1={x1} x2={x2} y1={yC} y2={yC} stroke={color} strokeWidth={3} strokeLinecap="round" opacity={0.5} />
              <EventGlyph x={x1} y={yC} ev={trigger!} onOpenCatch={onOpenCatch} onHover={onHover} onPin={onPin} r={6.5} focused={isFocused} />
              <EventGlyph x={x2} y={yC} ev={complete!} onOpenCatch={onOpenCatch} onHover={onHover} onPin={onPin} r={6.5} focused={isFocused} />
            </g>
          )
        }
        const ev = trigger || complete
        if (!ev) return null
        const t = ev.eventTimestamp.getTime()
        if (t < viewStart || t > viewEnd) return null
        return <EventGlyph key={cid} x={tToX(t, viewStart, viewEnd, canvasW)} y={yC} ev={ev} onOpenCatch={onOpenCatch} onHover={onHover} onPin={onPin} r={7.5} focused={isFocused} />
      })}
    </>
  )
}

function SimpleLaneContent({ items, viewStart, viewEnd, canvasW, onHover, onPin }: {
  items: DeviceEventSummary[]; viewStart: number; viewEnd: number; canvasW: number
  onHover: HoverSetter; onPin: PinSetter
}) {
  if (canvasW <= 0) return null
  const yC = 11
  let lastX = -100
  let altUp = true
  return (
    <>
      {items.map(ev => {
        const t = ev.eventTimestamp.getTime()
        if (t < viewStart || t > viewEnd) return null
        const x = tToX(t, viewStart, viewEnd, canvasW)
        let y = yC
        if (x - lastX < 14) { y += altUp ? -3.5 : 3.5; altUp = !altUp }
        else { altUp = true }
        lastX = x
        return <EventGlyph key={ev.id} x={x} y={y} ev={ev} r={7.5} onOpenCatch={() => {}} onHover={onHover} onPin={onPin} />
      })}
    </>
  )
}

function EventGlyph({ x, y, ev, onOpenCatch: _onOpenCatch, onHover, onPin, r = 7.5, focused = false }: {
  x: number; y: number; ev: DeviceEventSummary; r?: number; focused?: boolean
  onOpenCatch: (id: string) => void
  onHover: HoverSetter; onPin: PinSetter
}) {
  const meta = metaForEvent(ev)
  if (!meta) return null
  return (
    <g
      data-event-glyph
      transform={`translate(${x}, ${y})`}
      style={{ cursor: 'pointer', color: meta.color }}
      onClick={(e) => { e.stopPropagation(); onPin({ ev, x: e.clientX, y: e.clientY }) }}
      onMouseEnter={(e) => onHover({ ev, x: e.clientX, y: e.clientY })}
      onMouseMove={(e) => onHover({ ev, x: e.clientX, y: e.clientY })}
      onMouseLeave={() => onHover(null)}
      className="event-glyph"
    >
      {focused && <circle r={r + 4} fill="none" stroke={meta.color} strokeWidth={2.5} opacity={0.5} />}
      <circle r={r} fill="#fff" />
      <circle r={r} fill="none" stroke={meta.color} strokeWidth={focused ? 2.6 : 1.6} className="ring transition-[stroke-width] duration-100 hover:[stroke-width:2.6]" />
      <g dangerouslySetInnerHTML={{ __html: GLYPH_PATHS[meta.glyph] }} />
      <circle r={r + 4} fill="transparent" />
    </g>
  )
}

// Floating styled tooltip for hovered/pinned events. Pinned mode is
// interactive (pointer-events: auto) — user can highlight/copy text and
// click an explicit "Open in new tab" button.
function EventTooltip({ data, tz, pinned, onClose, onOpenCatch, onOpenBurst }: {
  data: { ev: DeviceEventSummary; x: number; y: number } | null
  tz: Tz; pinned: boolean
  onClose: () => void
  onOpenCatch: (id: string) => void
  onOpenBurst: (id: string) => void
}) {
  if (!data) return null
  const { ev, x, y } = data
  const meta = metaForEvent(ev)
  if (!meta) return null
  // Capture-related events offer an "Open in side panel" CTA.
  const isCaptureEvent = ev.eventType === 'capture_triggered' || ev.eventType === 'capture_complete' || ev.eventType === 'manual_capture_logged'
  const captureId = isCaptureEvent ? (ev.payload as { captureId?: string }).captureId : undefined

  // Field categorization: scalars → key/value row, objects/arrays → expanded JSON.
  type Field = { k: string; kind: 'scalar' | 'json'; display: React.ReactNode; raw: unknown }
  const fields: Field[] = []
  const isGps = ev.eventType === 'gps_fix'
  const isClockShift = ev.eventType === 'clock_shift'
  for (const [k, v] of Object.entries(ev.payload)) {
    if (k === 'type') continue
    if (v === null || v === undefined) continue
    if (Array.isArray(v) && v.length === 0) continue
    if (typeof v === 'object') {
      fields.push({ k, kind: 'json', display: JSON.stringify(v, null, 2), raw: v })
      continue
    }
    let display: React.ReactNode = String(v)
    if (typeof v === 'number' && k.toLowerCase().includes('byte')) display = `${(v / 1024).toFixed(1)} KB`
    if (typeof v === 'number' && k.toLowerCase().includes('duration')) display = `${v.toFixed(1)} s`
    // last_valid_fix_time is float epoch seconds — render as date/time.
    if (isGps && k === 'lastValidFixTime' && typeof v === 'number') {
      display = fmtFull(v * 1000, tz)
    }
    // clock_shift fields are float epoch seconds — show the before/after the
    // Pi was corrected to as readable dates, and the correction size as a
    // duration. Earlier events in this run were back-corrected by deltaSeconds.
    if (isClockShift && typeof v === 'number' &&
        (k === 'deviceTimeBeforeSeconds' || k === 'deviceTimeAfterSeconds')) {
      display = fmtFull(v * 1000, tz)
    }
    if (isClockShift && k === 'deltaSeconds' && typeof v === 'number') {
      display = fmtAge(v)
    }
    if (typeof v === 'boolean') display = v ? 'true' : 'false'
    fields.push({ k, kind: 'scalar', display, raw: v })
  }
  // Synthesized fixAge = event_timestamp − last_valid_fix_time.
  if (isGps) {
    const lvft = (ev.payload as { lastValidFixTime?: number | null }).lastValidFixTime
    if (typeof lvft === 'number') {
      const ageSec = ev.eventTimestamp.getTime() / 1000 - lvft
      fields.push({ k: 'fixAge', kind: 'scalar', display: fmtAge(ageSec), raw: ageSec })
    }
  }

  // GPS: display reverse-geocoded city if we have lat/lng.
  const gpsLat = ev.eventType === 'gps_fix' ? (ev.payload as { latitude?: number | null }).latitude : null
  const gpsLng = ev.eventType === 'gps_fix' ? (ev.payload as { longitude?: number | null }).longitude : null

  return (
    <div
      data-event-tooltip
      className={`fixed z-[100] bg-white border border-stone-300 rounded-md shadow-lg
        ${pinned ? 'pointer-events-auto' : 'pointer-events-none'}`}
      style={{ left: x + 14, top: y - 12, minWidth: 240, maxWidth: 360, padding: '11px 13px' }}
    >
      <div className="flex items-start gap-2">
        <div className="font-display font-extrabold uppercase tracking-[0.16em] text-[9.5px] text-ink-700 flex items-center gap-2 flex-1">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: meta.color }} />
          {meta.label}
        </div>
        {pinned && (
          <button onClick={onClose}
                  className="text-stone-400 hover:text-ink-700 transition-colors -mr-1 -mt-1 p-1"
                  title="close">
            <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3 L 9 9 M9 3 L 3 9" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
      <div className="text-[10.5px] text-stone-500 mt-1 mb-2 pb-1.5 border-b border-stone-100">
        {fmtFull(ev.eventTimestamp.getTime(), tz)}
      </div>
      {gpsLat != null && gpsLng != null && <GpsCity lat={gpsLat} lng={gpsLng} />}
      <div className="space-y-px">
        {fields.map(({ k, kind, display }) => kind === 'scalar' ? (
          <div key={k} className="flex justify-between gap-4 text-[11px]">
            <span className="text-stone-500">{k}</span>
            <span className="text-ink-700 font-medium font-mono tabular-nums truncate max-w-[220px]" title={String(display)}>{display}</span>
          </div>
        ) : (
          <details key={k} className="text-[10.5px]" open={pinned}>
            <summary className="text-stone-500 cursor-pointer hover:text-ink-700 list-none flex items-center gap-1">
              <span className="text-stone-400">▸</span>{k}
            </summary>
            <pre className="mt-1 mb-1 p-2 bg-stone-50 border border-stone-200 rounded text-[10.5px] text-ink-700 font-mono overflow-x-auto whitespace-pre max-h-60 overflow-y-auto">
              {String(display)}
            </pre>
          </details>
        ))}
      </div>
      {captureId && pinned && (
        <div className="mt-2.5 pt-2 border-t border-stone-100 flex items-center gap-4">
          <button
            onClick={() => onOpenCatch(captureId)}
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-bear hover:text-bear-dark font-semibold flex items-center gap-1.5 transition-colors"
          >
            Open in side panel
            <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 2 h8 v8 h-8 z" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 2 v8" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            onClick={() => onOpenBurst(captureId)}
            title="every trigger within 3 min of each other, frames and weight on one strip"
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-bear hover:text-bear-dark font-semibold transition-colors"
          >
            Burst view
          </button>
        </div>
      )}
      {!pinned && captureId && (
        <div className="mt-1 text-[9.5px] uppercase tracking-[0.14em] text-stone-400">
          click event to pin
        </div>
      )}
    </div>
  )
}

// Cursor-follow tooltip — pointer-events: none, panel-scoped (only renders
// rows for the panel currently under the cursor). Reads its own values out of
// `series` on demand so we don't redo every nearest-by-time lookup on every
// mousemove.
function TraceTooltip({ x, y, t, tz, units, series, panel }: {
  x: number; y: number; t: number; tz: Tz; units: 'g' | 'lb'
  series: Series; panel: string
}) {
  const rows: Array<[string, React.ReactNode, string?]> = []
  const fmt = (n: number | null, dp = 1, unit = '') => n == null ? '—' : `${n.toFixed(dp)}${unit}`
  const at = (arr: Pt[]) => nearestVal(arr, t)
  if (panel === 'weight') {
    const w = at(series.weight), ws = at(series.weightS)
    if (w != null)  rows.push(['weight', fmtWeight(w, units, 1), TRACE_COLORS.weight])
    if (ws != null) rows.push(['smoothed', fmtWeight(ws, units, 1), TRACE_COLORS.weightSmoothed])
  } else if (panel === 'cells-weight') {
    series.cellWeight.forEach((arr, i) => {
      const v = at(arr)
      if (v != null) rows.push([`cell ${i}`, fmtWeight(v, units, 1), channelColor(i, 'weight')])
    })
  } else if (panel === 'cells-raw') {
    series.cellRaw.forEach((arr, i) => {
      const v = at(arr)
      if (v != null) rows.push([`cell ${i}`, fmt(v, 0), channelColor(i, 'raw')])
    })
  } else if (panel === 'acc') {
    for (const [k, arr, c] of [['acc x', series.accX, TRACE_COLORS.accX], ['acc y', series.accY, TRACE_COLORS.accY], ['acc z', series.accZ, TRACE_COLORS.accZ]] as const) {
      const v = at(arr); if (v != null) rows.push([k, fmt(v, 2), c])
    }
  } else if (panel === 'gyr') {
    for (const [k, arr, c] of [['gyr x', series.gyrX, TRACE_COLORS.gyrX], ['gyr y', series.gyrY, TRACE_COLORS.gyrY], ['gyr z', series.gyrZ, TRACE_COLORS.gyrZ]] as const) {
      const v = at(arr); if (v != null) rows.push([k, fmt(v, 2), c])
    }
  } else if (panel === 'sys') {
    for (const [k, arr, c] of [['cpu', series.cpu, TRACE_COLORS.cpu], ['mem', series.mem, TRACE_COLORS.mem], ['swap', series.swap, TRACE_COLORS.swap]] as const) {
      const v = at(arr); if (v != null) rows.push([k, fmt(v, 1, '%'), c])
    }
  }
  if (rows.length === 0) return null
  return (
    <div
      className="fixed z-[90] bg-white border border-stone-200 rounded-md shadow-md pointer-events-none px-2.5 py-2"
      style={{ left: x + 14, top: y + 14, minWidth: 180, maxWidth: 280 }}
    >
      <div className="font-display font-extrabold text-[10px] text-ink-700 border-b border-stone-100 pb-1 mb-1.5 leading-none">
        {fmtFull(t, tz)}
      </div>
      {rows.map(([k, val, color]) => (
        <div key={k} className="flex justify-between gap-3 text-[10.5px] py-px">
          <span className="flex items-center gap-1.5 text-stone-500">
            {color && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />}
            {k}
          </span>
          <span className="text-ink-700 font-medium font-mono tabular-nums truncate">{val}</span>
        </div>
      ))}
    </div>
  )
}

// Reverse-geocode lat/lng to a city via BigDataCloud (free, no auth).
const _cityCache = new Map<string, string>()
const _cityInflight = new Map<string, Promise<string>>()
function GpsCity({ lat, lng }: { lat: number; lng: number }) {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`
  const [city, setCity] = useState<string | undefined>(_cityCache.get(key))
  useEffect(() => {
    if (_cityCache.has(key)) { setCity(_cityCache.get(key)); return }
    let cancelled = false
    let p = _cityInflight.get(key)
    if (!p) {
      p = fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`)
        .then(r => r.json())
        .then((d: { city?: string; locality?: string; principalSubdivision?: string; countryName?: string }) => {
          const parts = [d.city || d.locality, d.principalSubdivision, d.countryName].filter(Boolean) as string[]
          const out = parts.length > 0 ? parts.slice(0, 2).join(', ') : 'unknown'
          _cityCache.set(key, out)
          return out
        })
        .catch(() => 'lookup failed')
      _cityInflight.set(key, p)
    }
    p.then(c => { if (!cancelled) setCity(c) })
    return () => { cancelled = true }
  }, [key, lat, lng])
  return (
    <div className="flex justify-between gap-4 text-[11px] mb-1">
      <span className="text-stone-500">near</span>
      <span className="text-ink-700 font-medium truncate max-w-[220px]" title={city ?? 'looking up…'}>
        {city ?? <span className="text-stone-400">looking up…</span>}
      </span>
    </div>
  )
}

function LifecycleLines({ events, captureSpans, viewStart, viewEnd, canvasW, hidden }: {
  events: DeviceEventSummary[]; captureSpans: Record<string, { trigger?: DeviceEventSummary; complete?: DeviceEventSummary }>
  viewStart: number; viewEnd: number; canvasW: number; hidden: Set<string>
}) {
  if (canvasW <= 0) return null
  return (
    <svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none z-10">
      {/* Capture regions: faint colored bands */}
      {Object.entries(captureSpans).map(([cid, { trigger, complete }]) => {
        if (!trigger || !complete) return null
        if (hidden.has('capture_triggered') || hidden.has('capture_complete')) return null
        const t1 = trigger.eventTimestamp.getTime()
        const t2 = complete.eventTimestamp.getTime()
        if (t2 < viewStart || t1 > viewEnd) return null
        const x1 = tToX(t1, viewStart, viewEnd, canvasW)
        const x2 = tToX(t2, viewStart, viewEnd, canvasW)
        const ok = (complete.payload as { ok?: boolean }).ok !== false
        return (
          <rect key={cid} x={Math.min(x1, x2)} y={0} width={Math.abs(x2 - x1)} height="100%"
                fill={ok ? 'rgba(53,94,59,0.07)' : 'rgba(155,60,60,0.08)'} />
        )
      })}
      {/* Lifecycle dashed lines */}
      {events.map(ev => {
        const meta = metaForEvent(ev)
        if (!meta) return null
        const t = ev.eventTimestamp.getTime()
        if (t < viewStart || t > viewEnd) return null
        const x = tToX(t, viewStart, viewEnd, canvasW)
        return <line key={ev.id} x1={x} x2={x} y1={0} y2="100%" stroke={meta.color}
                     strokeWidth={1.4} strokeDasharray="4 3" opacity={0.5} />
      })}
    </svg>
  )
}

function Panel({ name, unit, height, yTicks, vMin, vMax, yTickFmt, samplingInfo, panelKey, outlierControl, outlierPct, onSetOutlierPct, children }: {
  name: string; unit: string; height: number
  yTicks: number[]; vMin: number; vMax: number
  yTickFmt?: (v: number) => string
  samplingInfo?: { shown: number; total: number } | null
  panelKey?: string
  outlierControl?: boolean; outlierPct?: number; onSetOutlierPct?: (pct: number) => void
  children: React.ReactNode
}) {
  const fmt = yTickFmt ?? ((v: number) => String(v))
  return (
    <div className="flex border-b border-stone-200 last:border-b-0" style={{ minHeight: height }} data-panel={panelKey}>
      <div className="flex-shrink-0 px-5 py-4 border-r border-stone-200 bg-stone-50/40 flex flex-col gap-3 justify-between"
           style={{ width: LABEL_W }}>
        <div>
          <div className="font-display font-extrabold text-[13px] text-ink-700 leading-none">{name}</div>
          <div className="text-[9.5px] text-stone-400 mt-1">{unit}</div>
        </div>
        {outlierControl && (
          <div className="flex flex-col gap-1">
            <div className="text-[9px] uppercase tracking-wide text-stone-400">exclude outliers</div>
            <div className="flex flex-col gap-0.5">
              {OUTLIER_LEVELS.map(lvl => {
                const sel = (outlierPct ?? 0) === lvl.pct
                return (
                  <button key={lvl.label} onClick={() => onSetOutlierPct?.(lvl.pct)}
                    title={lvl.pct > 0
                      ? `exclude the top/bottom ${(lvl.pct * 100).toFixed(0)}% (axis fits the ${lvl.label} range)`
                      : 'no exclusion — axis fits the full range'}
                    className="flex items-center gap-1 text-[9.5px] cursor-pointer group">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full border ${
                      sel ? 'bg-bear border-bear' : 'bg-white border-stone-300'
                    }`} />
                    <span className={sel ? 'text-ink-700' : 'text-stone-400 group-hover:text-stone-600'}>{lvl.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
      <div className="relative flex-1 bg-white overflow-hidden">
        {/* Y ticks */}
        {yTicks.map(v => {
          const range = vMax - vMin
          const innerH = height - 8
          const y = 4 + (1 - (v - vMin) / range) * innerH
          return (
            <div key={v} className={`absolute left-0 right-0 h-px pointer-events-none ${v === 0 ? 'bg-stone-200' : 'bg-stone-100'}`}
                 style={{ top: y }}>
              <span className="absolute left-2 -top-1.5 text-[9.5px] text-stone-400 bg-white px-1">{fmt(v)}</span>
            </div>
          )
        })}
        {/* Sampling indicator: "N / M" when reduction was applied. */}
        {samplingInfo && samplingInfo.total > samplingInfo.shown && (
          <span className="absolute top-1.5 right-2 font-mono text-[9.5px] text-stone-400 bg-white/80 px-1.5 py-0.5 rounded"
                title={`Server-side sampled: showing ${samplingInfo.shown} of ${samplingInfo.total} samples`}>
            {samplingInfo.shown.toLocaleString()} / {samplingInfo.total.toLocaleString()}
          </span>
        )}
        <svg className="absolute inset-0 w-full h-full overflow-visible">{children}</svg>
      </div>
    </div>
  )
}

function PathTrace({ data, viewStart, viewEnd, canvasW, h, vMin, vMax, color, hidden, strokeWidth = 1.4, opacity = 1 }: {
  data: Pt[]; viewStart: number; viewEnd: number; canvasW: number; h: number
  vMin: number; vMax: number; color: string; hidden?: boolean
  strokeWidth?: number; opacity?: number
}) {
  const dec = data.length > 1500 ? decimate(data, 1500) : data
  const d = canvasW > 0 ? pathFor(dec, viewStart, viewEnd, canvasW, h, vMin, vMax) : ''
  if (hidden) return null
  return <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} opacity={opacity}
               strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
}

function RailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3.5">
        <h3 className="font-display font-extrabold text-[10.5px] tracking-[0.20em] uppercase text-ink-700">{title}</h3>
        <span className="flex-1 h-px bg-stone-200" />
      </div>
      {children}
    </div>
  )
}

function ContinuousLegend({ hidden, toggle, hasChannels, showChannels, setShowChannels, nChannels }: {
  hidden: Set<string>; toggle: (key: string) => void
  hasChannels: boolean; showChannels: boolean; setShowChannels: (v: boolean) => void; nChannels: number
}) {
  const groups: Array<{ heading?: string; items?: { key: string; label: string; color: string }[] }> = [
    { items: [
      { key: 'weight',   label: 'weight',          color: TRACE_COLORS.weight },
      { key: 'weight-s', label: 'weight smoothed', color: TRACE_COLORS.weightSmoothed },
    ]},
    { heading: 'IMU acceleration' },
    { items: [
      { key: 'acc-x', label: 'acc · x', color: TRACE_COLORS.accX },
      { key: 'acc-y', label: 'acc · y', color: TRACE_COLORS.accY },
      { key: 'acc-z', label: 'acc · z', color: TRACE_COLORS.accZ },
    ]},
    { heading: 'IMU gyroscope' },
    { items: [
      { key: 'gyr-x', label: 'gyr · x', color: TRACE_COLORS.gyrX },
      { key: 'gyr-y', label: 'gyr · y', color: TRACE_COLORS.gyrY },
      { key: 'gyr-z', label: 'gyr · z', color: TRACE_COLORS.gyrZ },
    ]},
    { heading: 'system' },
    { items: [
      { key: 'cpu',  label: 'cpu %',  color: TRACE_COLORS.cpu  },
      { key: 'mem',  label: 'mem %',  color: TRACE_COLORS.mem  },
      { key: 'swap', label: 'swap %', color: TRACE_COLORS.swap },
    ]},
  ]
  return (
    <div>
      {groups.map((g, i) => (
        <div key={i}>
          {g.heading && (
            <div className="text-[9.5px] tracking-[0.16em] uppercase text-stone-400 font-semibold mt-3.5 mb-1.5 px-1.5">
              {g.heading}
            </div>
          )}
          {g.items?.map(it => (
            <button key={it.key}
                    onClick={() => toggle(it.key)}
                    className={`w-full flex items-center gap-3 px-1.5 py-1.5 rounded text-left transition-colors hover:bg-stone-50
                      ${hidden.has(`cont:${it.key}`) ? 'opacity-40' : ''}`}>
              <span className="w-[18px] h-[3px] rounded-sm flex-shrink-0" style={{ background: it.color }} />
              <span className="text-[11px] text-ink-700 font-medium">{it.label}</span>
            </button>
          ))}
        </div>
      ))}
      {hasChannels && (
        <>
          <div className="text-[9.5px] tracking-[0.16em] uppercase text-stone-400 font-semibold mt-3.5 mb-1.5 px-1.5">load cells</div>
          <button onClick={() => setShowChannels(!showChannels)}
                  className="w-full flex items-center gap-3 px-1.5 py-1.5 rounded text-left transition-colors hover:bg-stone-50">
            <span className={`w-[18px] h-[3px] rounded-sm flex-shrink-0 ${showChannels ? 'bg-bear' : 'bg-stone-300'}`} />
            <span className="text-[11px] text-ink-700 font-medium">{showChannels ? 'hide' : 'show'} channels ({nChannels})</span>
          </button>
        </>
      )}
    </div>
  )
}

function DiscreteLegend({ hidden, toggle, counts }: { hidden: Set<string>; toggle: (t: string) => void; counts: Record<string, number> }) {
  const order: string[] = [
    'app_start', 'app_stop',
    'capture_triggered', 'capture_complete',
    'tare', 'clock_shift', 'calibration_saved', 'signs_saved', 'config_changed',
    'imu_tap', 'imu_tap_sequence_complete',
    'camera_error', 'error_state_entered', 'error_state_cleared', 'data_push_complete',
    'wifi_status_change', 'wifi_client_change',
  ]
  return (
    <div>
      {order.map(t => {
        const meta = TYPE_META[t as DeviceEventType]
        if (!meta) return null
        return (
          <button key={t}
                  onClick={() => toggle(t)}
                  className={`w-full flex items-center gap-3 px-1.5 py-1.5 rounded text-left transition-colors hover:bg-stone-50
                    ${hidden.has(t) ? 'opacity-40' : ''}`}>
            <span className="w-[18px] h-[18px] flex-shrink-0 grid place-items-center"><GlyphSvg name={meta.glyph} color={meta.color} size={16}/></span>
            <span className="flex-1 text-[11px] text-ink-700 font-medium">{meta.label}</span>
            <span className="text-[9.5px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded font-medium">{counts[t] || 0}</span>
          </button>
        )
      })}
    </div>
  )
}

function RunsList({ runs, tz, onClick }: {
  runs: { runId: string; firstSeen: number; lastSeen: number }[]
  tz: Tz
  onClick: (r: { firstSeen: number; lastSeen: number }) => void
}) {
  if (runs.length === 0) return <div className="text-[10.5px] text-stone-400 px-1.5">No runs in window.</div>
  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden bg-white">
      {runs.map(r => (
        <button key={r.runId} onClick={() => onClick(r)}
                className="w-full px-3.5 py-2.5 border-b border-stone-200 last:border-b-0 hover:bg-stone-50 transition-colors text-left flex flex-col gap-0.5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-display font-extrabold text-[13px] text-ink-700 leading-none">
              {fmtDate(r.firstSeen, tz)} {fmtTimeShort(r.firstSeen, tz)}
            </span>
            <span className="text-[10px] text-stone-500">
              {fmtDuration((r.lastSeen - r.firstSeen) / 1000)}
            </span>
          </div>
          <div className="text-[9.5px] text-stone-400 truncate">{r.runId}</div>
        </button>
      ))}
    </div>
  )
}

function Stats({ events }: { events: DeviceEventSummary[] }) {
  const total = events.length
  const captures = events.filter(e => e.eventType === 'capture_complete' && (e.payload as { ok?: boolean }).ok !== false).length
  const errors   = events.filter(e => e.eventType === 'camera_error' || e.eventType === 'error_state_entered' || (e.eventType === 'capture_complete' && (e.payload as { ok?: boolean }).ok === false)).length
  const runs     = new Set(events.map(e => e.runId).filter(Boolean)).size
  return (
    <div className="grid grid-cols-2 border border-stone-200 rounded-lg overflow-hidden bg-white">
      <div className="px-3.5 py-3 border-r border-b border-stone-200">
        <div className="text-[9px] tracking-[0.18em] uppercase text-stone-500 font-semibold">events</div>
        <div className="font-display font-extrabold text-[18px] text-ink-700 mt-1">{total.toLocaleString()}</div>
      </div>
      <div className="px-3.5 py-3 border-b border-stone-200">
        <div className="text-[9px] tracking-[0.18em] uppercase text-stone-500 font-semibold">runs</div>
        <div className="font-display font-extrabold text-[18px] text-ink-700 mt-1">{runs}</div>
      </div>
      <div className="px-3.5 py-3 border-r border-stone-200">
        <div className="text-[9px] tracking-[0.18em] uppercase text-stone-500 font-semibold">captures</div>
        <div className="font-display font-extrabold text-[18px] text-pine mt-1">{captures}</div>
      </div>
      <div className="px-3.5 py-3">
        <div className="text-[9px] tracking-[0.18em] uppercase text-stone-500 font-semibold">errors</div>
        <div className={`font-display font-extrabold text-[18px] mt-1 ${errors > 0 ? 'text-bear' : 'text-stone-300'}`}>{errors}</div>
      </div>
    </div>
  )
}
